/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageToast from "sap/m/MessageToast";
import Dialog from "sap/m/Dialog";
import DatePicker from "sap/m/DatePicker";
import Select from "sap/m/Select";
import Input from "sap/m/Input";
import StandardListItem from "sap/m/StandardListItem";
import SelectDialog from "sap/m/SelectDialog";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ListBinding from "sap/ui/model/ListBinding";
import Item from "sap/ui/core/Item";
import Device from "sap/ui/Device";
import Konva from "konva";
import BaseController from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import Control from "sap/ui/core/Control";
import Event from "sap/ui/base/Event";
import CheckBox from "sap/m/CheckBox";
import formatter from "../src/utils/formatter";
import ComboBox from "sap/m/ComboBox";
import Theming from "sap/ui/core/Theming";
import {
    Seat,
    SeatInfo,
    KonvaSeat,
    BookingModel,
    Room,
    RoomInfo,
    KonvaRoom,
    FloorPlan
} from "../src/types";
import { transformToKonvaRooms } from "../src/utils/RoomDataTransFormer";
import { RoomUtils } from "../src/utils/RoomUtils";
import { HomeZoneAuthUtil } from "../src/utils/HomeZoneAuthUtil";
import Log from "sap/base/Log";
import UIFeedbackHelper from "../src/utils/UIFeedbackHelper";

import { TIME_RANGES, BUSINESS_HOURS } from "../src/constants/TimeConstants";
import {
    parseTimeToMinutes,
    isTimeGreaterOrEqual,
    getCurrentTimePlusMinutes,
    calculateEndTimeFromStart,
    validateAndAdjustStartTime,
    parseDatePickerValue,
    getStartOfDay,
    getEndOfDay,
    getISODateString,
    getMaxBookingDate
} from "../src/utils/TimeUtils";

// Time range constants

type SelectedSeatData = BookingModel | Seat;

/**
 * @namespace de.realcore.realseats.controller
 */
export default class GraphicController extends BaseController {
    private readonly formatter = formatter;
    private _stage!: Konva.Stage;
    private _layer!: Konva.Layer;
    private _contentGroup!: Konva.Group;
    private selectedEmployeeId: string | null = null;
    private employeeModel!: JSONModel;
    private selectedSeatModel: JSONModel;
    private oDate: Date;
    private startTime: Date;
    private endTime: Date;
    private myBookingsModel!: JSONModel;
    private BookedSeatsModel!: JSONModel;
    private _tooltipLayer: Konva.Layer;
    private _tooltip: Konva.Label;
    private _domTooltip: HTMLDivElement | null = null;
    private floorplansModel!: JSONModel;
    private _dialog: Dialog | null = null;
    private _graphicBookedDialog: Dialog | null = null; // Separate dialog for GraphicBooked
    private _searchHighlightLayer!: Konva.Layer;
    private readonly _conferenceRoomIds: Set<string> = new Set();
    private _employeeDialog: Dialog | null = null;
    private cancelDialog: Dialog | null = null; // Standard property name used by other controllers
    private selectedEmployeeFromDialog: string | null = null;

    // Cache for fetched data to prevent duplicate API calls
    private _cachedRooms: Room[] | null = null;
    private _cachedSeats: Seat[] | null = null;
    private _cachedFloorplans: FloorPlan[] | null = null;
    private _cachedBookings: any = null;
    private _cachedEmployees: any[] | null = null;
    private _cachedMyBookings: any[] | null = null;
    private _cachedBookingsForOthers: any[] | null = null;
    private _currentBookingTimespan: string | null = null;
    private _isLoadingRooms: boolean = false;
    private _isLoadingSeats: boolean = false;
    private _isLoadingFloorplans: boolean = false;
    private _isLoadingBookings: boolean = false;
    private _isLoadingEmployees: boolean = false;
    private readonly _isLoadingMyBookings: boolean = false;
    private _isLoadingBookingsForOthers: boolean = false;
    private _cachedRoomBookings: { [key: string]: any[] } = {};
    private _isPinchZooming: boolean = false;
    private readonly DESIGN_BASE = { WIDTH: 990, HEIGHT: 425 };

    // Performance optimization cache for seat availability
    private _seatAvailabilityCache: Map<
        string,
        { morning: boolean; afternoon: boolean; hasBooking: boolean }
    > = new Map();

    onInit(): void {
        // Initialize HomeZoneAuthUtil with API service
        HomeZoneAuthUtil.initialize(this.getApiService());

        // Load employee data for the SeatBookingDialog fragment
        this.loadEmployeeData();

        // Initialize filter models for enhanced filtering
        this.initializeFilterModels();

        this.getOwnerComponent()
            .getRouter()
            .getRoute("Graphic")
            .attachPatternMatched(this._onRouteMatched.bind(this));
        this.getOwnerComponent()
            .getRouter()
            .getRoute("InitialView")
            .attachPatternMatched(this._onRouteMatched.bind(this));

        // Subscribe to theme change events
        this.getOwnerComponent()
            .getEventBus()
            .subscribe("App", "ThemeChanged", this.onThemeChanged.bind(this));
        const oDatePicker = this.byId("idGraphicDatePicker") as DatePicker;
        if (oDatePicker) {
            const today = new Date();
            oDatePicker.setDateValue(today);

            // Initialize internal date properties
            this.oDate = today;
            this.startTime = new Date(today);
            this.startTime.setHours(0, 0, 0, 0);
            this.endTime = new Date(today);
            this.endTime.setHours(23, 59, 59, 999);
        }

        // Initialize DialogData model for date picker constraints
        const dialogData = {
            minDate: new Date(),
            maxDate: new Date(getMaxBookingDate()) // Create fresh Date object to prevent mutation
        };
        const dialogDataModel = new JSONModel(dialogData);
        this.getView().setModel(dialogDataModel, "DialogData");

        this.onDatePickerGraphicChange();
    }

    /**
     * Checks if the application is currently in dark mode
     * @returns true if dark mode is active, false otherwise
     */
    private isDarkMode(): boolean {
        const currentTheme: string = Theming.getTheme();
        return currentTheme === "sap_horizon_dark";
    }

    /**
     * Checks if the current device is a touch device
     * @returns true if touch device, false otherwise
     */
    private isTouchDevice(): boolean {
        return Device.support.touch;
    }

    /**
     * Creates a DOM-based tooltip that floats above the canvas
     */
    private createDOMTooltip(): void {
        // Remove existing tooltip if any
        if (this._domTooltip) {
            this._domTooltip.remove();
        }

        // Create tooltip element
        // eslint-disable-next-line fiori-custom/sap-no-element-creation
        this._domTooltip = document.createElement("div");
        this._domTooltip.style.cssText = `
            position: fixed;
            z-index: 10000;
            pointer-events: none;
            visibility: hidden;
            background: ${this.isDarkMode() ? "#354a5f" : "#ffffff"};
            color: ${this.isDarkMode() ? "#ffffff" : "#32363a"};
            border: 1px solid ${this.isDarkMode() ? "#5a6c7d" : "#d9d9d9"};
            border-radius: 6px;
            padding: 8px 12px;
            font-family: '72', 'Helvetica Neue', Arial, sans-serif;
            font-size: 12px;
            line-height: 1.3;
            box-shadow: ${this.isDarkMode() ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.15)"};
            max-width: 250px;
            width: auto;
            height: auto;
            min-height: 0;
            max-height: none;
            overflow: visible;
            white-space: pre;
            opacity: 0.95;
            transition: opacity 0.15s ease;
            font-weight: normal;
            box-sizing: border-box;
            display: inline-block;
            vertical-align: top;
        `;

        document.body.appendChild(this._domTooltip);
    }

    /**
     * Updates DOM tooltip styling based on current theme
     */
    private updateDOMTooltipTheme(): void {
        if (!this._domTooltip) {
            return;
        }

        const isDark = this.isDarkMode();
        this._domTooltip.style.background = isDark ? "#354a5f" : "#ffffff";
        this._domTooltip.style.color = isDark ? "#ffffff" : "#32363a";
        this._domTooltip.style.borderColor = isDark ? "#5a6c7d" : "#d9d9d9";
        this._domTooltip.style.boxShadow = isDark
            ? "0 2px 12px rgba(0, 0, 0, 0.5)"
            : "0 2px 12px rgba(0, 0, 0, 0.15)";
    }

    /**
     * Handles theme change events from EventBus
     * @param sChannelId The channel ID
     * @param sEventId The event ID
     * @param oData Event data containing theme information
     */
    public onThemeChanged(sChannelId: string, sEventId: string, oData: any): void {
        this.refreshFloorplan();
    }

    /**
     * Refreshes the graphics when theme changes to update all visual elements
     */
    public refreshFloorplan(): void {
        if (this._layer) {
            // Update all existing visual elements for new theme
            this.updateExistingElementsTheme();

            // Update search highlight colors for theme changes
            this.updateSearchHighlightTheme();

            // Clear and reload the floorplan with the new theme, then restore seat colors
            void this.loadFloorplan()
                .then(() => {
                    // Ensure seat colors are restored after floorplan reload during theme change
                    void this.markBookedSeats();
                })
                .catch((error) => {
                    Log.error("Error refreshing floorplan:", error as Error);
                });
        }
    }

    private initializeFilterModels(): void {
        const oView = this.getView();

        // Initialize models for filter dependencies
        const locationFloorModel = new JSONModel({
            items: [],
            selectedKey: "", // Single selection for graphic view
            itemCount: 0,
            itemUnit: "items"
        });

        oView.setModel(locationFloorModel, "LocationFloorModel");

        // Load floor data for the location-floor filter
        this.loadLocationFloorData().catch((error) => {
            Log.error("Error loading location-floor data:", error as Error);
        });
    }

    private async loadLocationFloorData(): Promise<void> {
        try {
            await this.fetchAllFloors();
            const floorplansModel = this.getView().getModel("FloorplanModel") as JSONModel;

            if (floorplansModel) {
                const floorplans = floorplansModel.getData().floorplan || [];
                const locationFloorItems = floorplans.map((fp: any) => ({
                    key: fp.id,
                    text: fp.name,
                    groupName: fp.location?.name || "Unknown Location"
                }));

                const locationFloorModel = this.getView().getModel(
                    "LocationFloorModel"
                ) as JSONModel;
                locationFloorModel.setProperty("/items", locationFloorItems);
                locationFloorModel.setProperty("/itemCount", locationFloorItems.length);
            }
        } catch (error) {
            Log.error("Error creating location-floor items:", error as Error);
        }
    }

    onAfterRendering(): void {
        // Initialize Konva stage with container size
        const wrapperEl = document.getElementById("graphic-wrapper") as HTMLElement | null;
        const containerEl = document.getElementById("graphic") as HTMLElement | null;
        const rect = (wrapperEl || containerEl)?.getBoundingClientRect?.();
        const initWidth = Math.max(0, Math.floor(rect?.width || window.innerWidth));
        const initHeight = Math.max(0, Math.floor(rect?.height || window.innerHeight - 250));

        this._stage = new Konva.Stage({
            container: "graphic", // The ID of the container element
            width: initWidth,
            height: initHeight
        });

        this._layer = new Konva.Layer();
        this._stage.add(this._layer);

        // Create DOM-based tooltip only on non-touch devices
        if (!this.isTouchDevice()) {
            this.createDOMTooltip();
        }

        this._tooltipLayer = new Konva.Layer();
        this._stage.add(this._tooltipLayer);

        // Create search highlight layer for search indicators
        this._searchHighlightLayer = new Konva.Layer();
        this._stage.add(this._searchHighlightLayer);

        this._tooltip = new Konva.Label({
            visible: false,
            listening: false // ignore mouse events
        });

        // Create theme-aware tooltip background
        const isDark = this.isDarkMode();
        this._tooltip.add(
            new Konva.Tag({
                fill: isDark ? "#354a5f" : "#ffffff",
                stroke: isDark ? "#5a6c7d" : "#d9d9d9",
                strokeWidth: 1,
                opacity: 0.95,
                cornerRadius: 8,
                shadowColor: isDark ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.15)",
                shadowBlur: 12,
                shadowOffset: { x: 0, y: 2 },
                shadowOpacity: 1
            })
        );

        this._tooltip.add(
            new Konva.Text({
                text: "",
                fontFamily: "'72', 'Helvetica Neue', Arial, sans-serif",
                fontSize: 13,
                padding: 12,
                fill: isDark ? "#ffffff" : "#32363a",
                lineHeight: 1.4,
                align: "left"
            })
        );

        this._tooltipLayer.add(this._tooltip);
        this._tooltipLayer.draw();

        // Create a content group that we will scale uniformly to keep floorplan and seats aligned
        this._contentGroup = new Konva.Group({ x: 0, y: 0 });
        this._layer.add(this._contentGroup);

        // Setup interactive graphics AFTER Konva stage is ready
        this.setupInteractiveGraphic();

        // Resize stage to container initially and on window resize
        this._resizeStageToContainer();
        window.addEventListener("resize", this._onWindowResize);

        // Setup observer for DynamicPage header state changes
        this._setupDynamicPageObserver();

        // Set default floor selection for both old and new controls
        const defaultFloorId = "55cad9da-8795-4f25-b84b-78dca8b5c85c";

        const oFloorSelect = this.byId("idGraphicFloorSelect") as Select;
        if (oFloorSelect) {
            oFloorSelect.setSelectedKey(defaultFloorId);
        }

        const oLocationFloorCombo = this.byId("idItemsLocationFloorComboBox") as ComboBox;
        if (oLocationFloorCombo) {
            oLocationFloorCombo.setSelectedKey(defaultFloorId);
        }

        // Update the LocationFloorModel
        const locationFloorModel = this.getView().getModel("LocationFloorModel") as JSONModel;
        if (locationFloorModel) {
            locationFloorModel.setProperty("/selectedKey", defaultFloorId);
        }

        // Load the default floorplan
        this.loadRooms()
            .then(async () => {
                // Load floorplan first to show the visual structure
                await this.loadFloorplan();

                // Then fetch booking data and update seat colors
                try {
                    await Promise.all([this.FetchBookingsSpaces(), this.fetchMyBookings()]);

                    // Update seat colors after booking data is loaded
                    await this.markBookedSeats();
                } catch (error) {
                    UIFeedbackHelper.showErrorMessage(
                        `Error fetching booking data: ${(error as Error).message}`
                    );
                    // Even if booking data fails, still show seats with default colors
                    await this.markBookedSeats();
                }
            })
            .catch((error) => {
                UIFeedbackHelper.showErrorMessage(
                    `Error loading rooms on route match: ${(error as Error).message}`
                );
                // Even if there's an error, still load the floorplan
                void this.loadFloorplan();
            });
    }

    private _onRouteMatched(...args: any[]): void {
        const routeArgs = args[0] as any;
        const selectedLocation = routeArgs.getParameter("arguments").location;
        const selectedFloor = routeArgs.getParameter("arguments").floor;

        if (selectedLocation) {
            const locationFloorComboBox = this.getView().byId(
                "itemsComboBoxLocationFloorSelection"
            ) as ComboBox;
            if (locationFloorComboBox) {
                locationFloorComboBox.setSelectedKey(`${selectedLocation}_${selectedFloor}`);
            }
        }

        // Load rooms and floorplan immediately for fast initial display - using incoming approach
        this.loadRooms()
            .then(async () => {
                // Load floorplan first to show the visual structure
                await this.loadFloorplan();

                // Then fetch booking data and update seat colors
                try {
                    await Promise.all([this.FetchBookingsSpaces(), this.fetchMyBookings()]);

                    // Update seat colors after booking data is loaded
                    await this.markBookedSeats();
                } catch (error) {
                    UIFeedbackHelper.showErrorMessage(
                        `Error fetching booking data: ${(error as Error).message}`
                    );
                    // Even if booking data fails, still show seats with default colors
                    await this.markBookedSeats();
                }
            })
            .catch((error) => {
                UIFeedbackHelper.showErrorMessage(
                    `Error loading rooms on route match: ${(error as Error).message}`
                );
                // Even if there's an error, still load the floorplan
                void this.loadFloorplan();
            });
    }
    private async fetchAllRooms(): Promise<Room[]> {
        if (this._cachedRooms) {
            return this._cachedRooms;
        }

        if (this._isLoadingRooms) {
            while (this._isLoadingRooms) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            return this._cachedRooms || [];
        }

        try {
            this._isLoadingRooms = true;
            const apiService = this.getApiService();
            const rooms = await apiService.room.fetchAll();
            this._cachedRooms = rooms;
            return rooms;
        } catch (error) {
            Log.error("Error fetching Rooms:", error as Error);
            return [];
        } finally {
            this._isLoadingRooms = false;
        }
    }
    private async fetchAllSeats(): Promise<Seat[]> {
        if (this._cachedSeats) {
            return this._cachedSeats;
        }

        if (this._isLoadingSeats) {
            while (this._isLoadingSeats) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            return this._cachedSeats || [];
        }

        try {
            this._isLoadingSeats = true;
            const apiService = this.getApiService();
            const seats = await apiService.seat.fetchAll();
            this._cachedSeats = seats;
            return seats;
        } catch (error) {
            Log.error("Error fetching Seats:", error as Error);
            return [];
        } finally {
            this._isLoadingSeats = false;
        }
    }

    /**
     * Loads conference rooms from cached room data (no additional API call)
     * Uses the same logic as RoomApi to exclude flex/training rooms
     */
    private async loadConferenceRooms(): Promise<void> {
        try {
            const allRooms = this._cachedRooms || (await this.fetchAllRooms());

            // Use centralized conference room detection logic
            const conferenceRooms = RoomUtils.filterConferenceRooms(allRooms);

            // Store conference room IDs in a Set for fast lookup
            this._conferenceRoomIds.clear();
            conferenceRooms.forEach((room: Room) => {
                this._conferenceRoomIds.add(room.id);
            });

            Log.info(
                `Loaded ${conferenceRooms.length} conference rooms from cache: ${conferenceRooms.map((r: Room) => r.name).join(", ")}`
            );
        } catch (error) {
            Log.error("Error loading conference rooms:", error as Error);
        }
    }

    /**
     * Checks if a room is a conference room using the dynamically loaded data
     * @param roomId The room ID to check
     * @returns true if the room is a conference room
     */
    private isConferenceRoom(roomId: string): boolean {
        return this._conferenceRoomIds.has(roomId);
    }

    private async loadRooms(): Promise<void> {
        try {
            const rooms: Room[] = await this.fetchAllRooms();
            const seats: Seat[] = await this.fetchAllSeats();

            await this.loadConferenceRooms();

            // Store the original room data for lookup by room ID
            const roomDataMap: Record<string, Room> = {};
            rooms.forEach((room) => {
                roomDataMap[room.id] = room;
            });
            // Store room data map in a model for later access
            const roomDataModel = new JSONModel(roomDataMap);
            this.getView().setModel(roomDataModel, "RoomDataMap");

            // Store the original seat data with room details for lookup by seat ID
            const seatDataMap: Record<string, Seat> = {};
            seats.forEach((seat) => {
                seatDataMap[seat.id] = seat;
            });

            // Store seat data map in a model for later access
            const seatDataModel = new JSONModel(seatDataMap);
            this.getView().setModel(seatDataModel, "SeatDataMap");

            const konvaRoomsByFloor: Record<string, KonvaRoom[]> = transformToKonvaRooms(
                rooms,
                seats
            );

            const oModel = new JSONModel(konvaRoomsByFloor);
            this.getView().setModel(oModel, "RoomData");

            // this.createRoomsFromJSON("55cad9da-8795-4f25-b84b-78dca8b5c85c");
        } catch (error) {
            Log.error("Error loading rooms:", error as Error);
        }
    }

    /**
     * Processes SVG content to make walls white in dark mode
     * @param svgUrl The URL to the SVG file
     * @returns Promise resolving to a data URL with the processed SVG
     */
    private async processSvgForDarkMode(floorID: string): Promise<string> {
        const apiService = this.getApiService();
        const svg = await apiService.floorplan.fetchSVGById(floorID);
        if (!this.isDarkMode()) {
            return URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
        }

        try {
            // Fetch the SVG content

            // Define white color for dark mode replacement
            const whiteColor = "white";

            // Replace black strokes with white strokes for walls
            const modifiedSvg = svg
                .replace(/stroke="#231f20"/g, `stroke="${whiteColor}"`)
                .replace(/stroke:#231f20/g, `stroke:${whiteColor}`)
                .replace(/stroke:#000000/g, `stroke:${whiteColor}`)
                .replace(/stroke:black/g, `stroke:${whiteColor}`)
                .replace(/stroke="black"/g, `stroke="${whiteColor}"`)
                .replace(/stroke="#000000"/g, `stroke="${whiteColor}"`)
                .replace(/stroke="#000"/g, `stroke="${whiteColor}"`);

            // Create a data URL from the modified SVG
            const blob = new Blob([modifiedSvg], { type: "image/svg+xml" });
            return URL.createObjectURL(blob);
        } catch (error) {
            Log.error("Error processing SVG for dark mode:", error as Error);
            return URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" })); // Fallback to original URL
        }
    }

    private readonly canvasElementId = "konvaFloorplanCanvas";
    private async createFloorplanImage(selectedFloor?: string): Promise<void> {
        const canvas = document.getElementById(this.canvasElementId) as HTMLCanvasElement;
        if (!canvas) {
            Log.error("Error: Canvas element not found for existing HTML control.");
            return;
        }

        return new Promise<void>((resolve, reject) => {
            try {
                // Process SVG for dark mode if needed
                this.processSvgForDarkMode(selectedFloor).then((processedUrl) => {
                    const imageObj = new Image();
                    imageObj.src = processedUrl;

                    imageObj.onload = () => {
                        const scale = 2;

                        // enlarge the canvas
                        canvas.width = imageObj.width * scale;
                        canvas.height = imageObj.height * scale;

                        const ctx = canvas.getContext("2d");
                        if (!ctx) {
                            Log.error("Error: 2D context not available on canvas.");
                            resolve();
                            return;
                        }

                        // scale the context so the image is drawn at the higher resolution
                        ctx.scale(scale, scale);
                        ctx.drawImage(imageObj, 0, 0);

                        if (this.isDarkMode()) {
                            // Here we apply a filter to convert dark pixels to white
                            console.log("Applying dark mode color filtering to canvas");
                            ctx.setTransform(1, 0, 0, 1, 0, 0);

                            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                            const data = imageData.data;

                            // Convert black/dark pixels to white
                            for (let i = 0; i < data.length; i += 4) {
                                const r = data[i];
                                const g = data[i + 1];
                                const b = data[i + 2];
                                const alpha = data[i + 3];

                                // If pixel is black or very dark (and not transparent)
                                if (alpha > 0 && r < 80 && g < 80 && b < 80) {
                                    data[i] = 255; // R
                                    data[i + 1] = 255; // G
                                    data[i + 2] = 255; // B
                                }
                            }

                            ctx.putImageData(imageData, 0, 0);
                        }

                        // create Konva image from the high-res canvas at design size; scaling is applied via the content group
                        const floorplan = new Konva.Image({
                            image: canvas,
                            width: this.DESIGN_BASE.WIDTH,
                            height: this.DESIGN_BASE.HEIGHT
                        });

                        if (this._contentGroup) {
                            this._contentGroup.removeChildren();
                            this._contentGroup.add(floorplan);
                        } else {
                            this._layer.removeChildren();
                            this._layer.add(floorplan);
                        }
                        this._layer.draw();
                        this._applyDesignScale();

                        const floor =
                            selectedFloor ||
                            (this.byId("idGraphicFloorSelect") as Select)?.getSelectedKey();

                        if (!floor) {
                            Log.error(
                                "[createFloorplanImage] No floor selected for createRoomsFromJSON"
                            );
                            resolve();
                            return;
                        }

                        this.createRoomsFromJSON(floor);
                        resolve();
                    };

                    imageObj.onerror = () => {
                        Log.error("Error loading floorplan image:", processedUrl);
                        reject(new Error("Failed to load floorplan image"));
                    };
                });
            } catch (error) {
                Log.error("Error in createFloorplanImage:", error as Error);
                reject(error as Error);
            }
        });
    }

    private async FetchBookingsSpaces(): Promise<void> {
        const apiService = this.getApiService();
        const oDatePicker = this.getView().byId("idGraphicDatePicker") as DatePicker;

        // Use existing date properties if available, otherwise get from picker
        if (!this.oDate) {
            this.oDate = oDatePicker?.getDateValue() as Date;
        }

        // Fallback to today if no date is available
        if (!this.oDate || !(this.oDate instanceof Date) || isNaN(this.oDate.getTime())) {
            this.oDate = new Date();
            Log.warning("No valid date available for fetching bookings, using today");
        }

        // Ensure start and end times are properly set
        if (
            !this.startTime ||
            !this.endTime ||
            !(this.startTime instanceof Date) ||
            !(this.endTime instanceof Date) ||
            isNaN(this.startTime.getTime()) ||
            isNaN(this.endTime.getTime())
        ) {
            this.startTime = getStartOfDay(this.oDate);
            this.endTime = getEndOfDay(this.oDate);
        }

        const startTimeFormatted = formatter.formatLocalDateTime(this.startTime);
        const endTimeFormatted = formatter.formatLocalDateTime(this.endTime);
        const currentTimespan = `${startTimeFormatted}-${endTimeFormatted}`;

        // Check if we already have cached data for this timespan
        if (this._cachedBookings && this._currentBookingTimespan === currentTimespan) {
            // Rebuild the seat availability cache from cached booking data
            this.rebuildSeatAvailabilityCache(this._cachedBookings);

            this.BookedSeatsModel = new JSONModel({ bookedSeats: this._cachedBookings });
            this.getView().setModel(this.BookedSeatsModel, "BookedSeatsModel");
            return;
        }

        // If currently loading this timespan, wait for it
        if (this._isLoadingBookings && this._currentBookingTimespan === currentTimespan) {
            // Wait for the current loading to complete
            while (this._isLoadingBookings) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            if (this._cachedBookings) {
                // Rebuild the seat availability cache from cached booking data
                this.rebuildSeatAvailabilityCache(this._cachedBookings);

                this.BookedSeatsModel = new JSONModel({ bookedSeats: this._cachedBookings });
                this.getView().setModel(this.BookedSeatsModel, "BookedSeatsModel");
            }
            return;
        }

        try {
            this._isLoadingBookings = true;
            this._currentBookingTimespan = currentTimespan;

            const bookedSeats = await apiService.booking.fetchSelectedDate(
                startTimeFormatted,
                endTimeFormatted
            );

            this._cachedBookings = this.preprocessBookingData(bookedSeats);

            // Rebuild the cache from the newly processed data
            this.rebuildSeatAvailabilityCache(this._cachedBookings);

            this.BookedSeatsModel = new JSONModel({
                bookedSeats: this._cachedBookings,
                timespan: currentTimespan
            });
            this.getView().setModel(this.BookedSeatsModel, "BookedSeatsModel");

            Log.info(
                `Successfully loaded and optimized booking data for timespan: ${currentTimespan}`
            );
        } catch (error) {
            Log.error("Error fetching BookedSeats:", error as Error);
        } finally {
            this._isLoadingBookings = false;
        }
    }

    /**
     * Preprocesses booking data for efficient multi-booking support
     * Enhances the existing API response structure with fast lookup maps
     * @param bookedSeats Raw booking data from API
     * @returns Enhanced booking data with availability pre-calculated
     */
    private preprocessBookingData(bookedSeats: any): any {
        if (!bookedSeats?.seats) {
            return bookedSeats;
        }

        const seatAvailabilityMap = new Map<
            string,
            { morning: boolean; afternoon: boolean; hasBooking: boolean }
        >();

        // Process each seat's booking data once
        const enhancedSeats = bookedSeats.seats.map((seat: any) => {
            const seatId = seat.seatId;
            const bookings = seat.bookedTimes || [];

            // Pre-calculate availability for this seat
            let morning = true;
            let afternoon = true;
            let isFullyBookedFromNow = false;
            let hasBookingsForSelectedDate = false;

            if (bookings.length > 0) {
                const now = new Date();
                const currentHour = now.getHours();

                // Check if any booking affects the selected day
                const selectedDate = this.oDate || now;
                const selectedDateString = selectedDate.toISOString().split("T")[0];
                const todayDateString = now.toISOString().split("T")[0];

                for (const booking of bookings) {
                    const startTime = new Date(booking.startTime);
                    const endTime = new Date(booking.endTime);
                    const bookingDateString = startTime.toISOString().split("T")[0];

                    // Only process bookings for the selected date
                    if (bookingDateString !== selectedDateString) {
                        continue;
                    }

                    // Mark that this seat has bookings for the selected date
                    hasBookingsForSelectedDate = true;

                    const startHour = startTime.getHours();
                    const endHour = endTime.getHours();
                    const endMinute = endTime.getMinutes();

                    // Check morning slot (8-12) - business hours before lunch
                    if (startHour < 12 && endHour > 8) {
                        morning = false;
                    }

                    // Check afternoon slot (13-17) - business hours after lunch
                    if (startHour < 17 && endHour > 13) {
                        afternoon = false;
                    }

                    // For today only: check if seat is unavailable from current time onwards
                    if (selectedDateString === todayDateString) {
                        const currentMinutes = currentHour * 60 + now.getMinutes();
                        const bookingStartMinutes = startHour * 60 + startTime.getMinutes();
                        const bookingEndMinutes = endHour * 60 + endMinute;

                        // Check if booking covers the remaining business hours for today
                        const businessEndMinutes = 17 * 60; // 17:00

                        // If booking starts before/at current time and ends at/after business end, it's fully booked from now
                        if (
                            bookingStartMinutes <= currentMinutes &&
                            bookingEndMinutes >= businessEndMinutes
                        ) {
                            isFullyBookedFromNow = true;
                        }
                    }
                }
            }

            // Always use the standard morning/afternoon logic for consistent behavior
            // This ensures seats show correct availability regardless of current time
            const useFromNowLogic = false;

            const isFullyBooked = useFromNowLogic
                ? isFullyBookedFromNow
                : !morning && !afternoon && hasBookingsForSelectedDate;

            // Set the availability for fast lookup later
            seatAvailabilityMap.set(seatId, {
                morning,
                afternoon,
                hasBooking: hasBookingsForSelectedDate
            });

            return {
                ...seat,
                // Add pre-calculated availability flags for instant access
                _availability: {
                    morning,
                    afternoon,
                    hasBooking: hasBookingsForSelectedDate,
                    isPartial:
                        (morning || afternoon) && hasBookingsForSelectedDate && !isFullyBooked,
                    isFullyBooked
                }
            };
        });

        this._seatAvailabilityCache = seatAvailabilityMap;

        return {
            ...bookedSeats,
            seats: enhancedSeats
        };
    }

    /**
     * Rebuilds the seat availability cache from cached booking data
     * This is needed when reusing cached booking data to ensure the cache stays synchronized
     * @param bookedSeats The cached booking data to rebuild cache from
     */
    private rebuildSeatAvailabilityCache(bookedSeats: any): void {
        if (!bookedSeats?.seats) {
            return;
        }

        const seatAvailabilityMap = new Map<
            string,
            { morning: boolean; afternoon: boolean; hasBooking: boolean }
        >();

        // Extract availability data from cached seats
        bookedSeats.seats.forEach((seat: any) => {
            if (seat._availability) {
                seatAvailabilityMap.set(seat.seatId, {
                    morning: seat._availability.morning,
                    afternoon: seat._availability.afternoon,
                    hasBooking: seat._availability.hasBooking
                });
            }
        });

        this._seatAvailabilityCache = seatAvailabilityMap;
    }

    /**
     * Extracts booking data for a specific seat from the cached BookedSeatsModel
     * @param seatId The ID of the seat to get booking data for
     * @returns The booking data for the seat or null if not found
     */
    private getBookingData(seatId: string): object | null {
        const bookedSeatsData = this.BookedSeatsModel?.getData()?.bookedSeats?.seats || [];

        // Find the seat in the cached booking data
        const seatBookingData = bookedSeatsData.find((seat: any) => seat.seatId === seatId);

        if (!seatBookingData?.bookedTimes?.length) {
            return null;
        }

        // Get the first active booking
        const activeBooking = seatBookingData.bookedTimes[0];

        if (!activeBooking) {
            return null;
        }

        // Enhance booking data with room details
        // Pass null as seatData to force using RoomDataMap fallback
        this.enhanceBookingDataWithRoomDetails(activeBooking, null);

        // Initialize cancelDescription if not present
        if (!activeBooking.cancelDescription) {
            activeBooking.cancelDescription = "";
        }

        // Transform the cached data to match the expected structure for the dialog
        return {
            id: activeBooking.bookingId,
            startTime: new Date(activeBooking.startTime),
            endTime: new Date(activeBooking.endTime),
            description: activeBooking.description,
            eventId: activeBooking.eventId,
            cancelled: activeBooking.cancelled || false,
            cancelDescription: activeBooking.cancelDescription || "",
            seat: {
                id: seatBookingData.seatId,
                name: activeBooking.name || seatBookingData.name,
                position: seatBookingData.position,
                room: {
                    id: seatBookingData.roomId,
                    name: activeBooking.roomName || "Unknown Room",
                    description: null,
                    floorPlan: {
                        name: "Floor",
                        location: {
                            name: "Location"
                        }
                    }
                }
            },
            employee: {
                id: activeBooking.employeeId,
                firstName: activeBooking.givenName,
                lastName: activeBooking.surname,
                department: {
                    id: activeBooking.departmentId,
                    name: activeBooking.departmentName
                }
            },
            bookingUser: {
                id: activeBooking.bookingUserId,
                firstName: activeBooking.bookingUserFirstName,
                lastName: activeBooking.bookingUserLastName,
                department: {
                    id: activeBooking.bookingUserDepartmentId,
                    name: activeBooking.bookingUserDepartmentName
                }
            },
            // Add all bookings for multi-booking support
            allBookings: seatBookingData.allBookings || []
        };
    }

    /**
     * Gets all bookings for a specific seat for multi-booking display
     * @param seatId The ID of the seat to get all bookings for
     * @returns Array of all bookings for the seat or empty array if none found
     */
    private getAllBookingsForSeat(seatId: string): any[] {
        const bookedSeatsData = this.BookedSeatsModel?.getData()?.bookedSeats?.seats || [];
        const seatBookingData = bookedSeatsData.find((seat: any) => seat.seatId === seatId);
        if (!seatBookingData?.bookedTimes?.length) {
            return [];
        }

        // Transform all bookings to include full data structure expected by the fragment
        const transformedBookings = seatBookingData.bookedTimes.map((booking: any) => {
            const transformed = {
                id: booking.bookingId,
                startTime: booking.startTime,
                endTime: booking.endTime,
                description: booking.description,
                eventId: booking.eventId,
                cancelled: booking.cancelled || false,
                seat: {
                    id: seatBookingData.seatId,
                    name: seatBookingData.name,
                    position: seatBookingData.position,
                    room: {
                        id: seatBookingData.roomId,
                        name: seatBookingData.roomId,
                        description: null as any,
                        floorPlan: {
                            name: "Floor",
                            location: {
                                name: "Location"
                            }
                        }
                    }
                },
                employee: {
                    id: booking.employeeId,
                    firstName: booking.givenName || booking.firstName || booking.employee_firstName,
                    lastName: booking.surname || booking.lastName || booking.employee_lastName,
                    department: {
                        id: booking.departmentId,
                        name: booking.departmentName
                    }
                },
                bookingUser: {
                    id: booking.bookingUserId,
                    firstName: booking.bookingUserFirstName || booking.bookingUser_firstName,
                    lastName: booking.bookingUserLastName || booking.bookingUser_lastName,
                    department: {
                        id: booking.bookingUserDepartmentId,
                        name: booking.bookingUserDepartmentName
                    }
                }
            };

            // Initialize properties that the CancelBookingDialog expects
            (transformed as any).cancelDescription = "";
            (transformed as any).cancelled = transformed.cancelled || false;

            // Enhance booking data with proper room details
            this.enhanceBookingDataWithRoomDetails(transformed, { room: null });

            // Add canCancel property to this booking
            this.addCanCancelProperty(transformed);
            return transformed;
        });

        return transformedBookings;
    }

    /**
     * Enhances booking data with detailed room information from cached room data
     * @param bookingData The booking data to enhance
     * @param seatData The seat data containing room details
     */
    private enhanceBookingDataWithRoomDetails(bookingData: any, seatData: any): void {
        if (seatData?.room) {
            // Update the room details with full information from seat data
            bookingData.seat.room = {
                id: seatData.room.id || bookingData.seat.room.id,
                name: seatData.room.name || seatData.room.id,
                description: seatData.room.description || null,
                floorPlan: {
                    id: seatData.room.floorPlan?.id,
                    name: seatData.room.floorPlan?.name || "Floor",
                    location: {
                        id: seatData.room.floorPlan?.location?.id,
                        name: seatData.room.floorPlan?.location?.name || "Location"
                    }
                }
            };
        } else {
            // Try to get room details from RoomDataMap if not available in seat data
            const roomDataModel = this.getView().getModel("RoomDataMap") as JSONModel;
            const roomData = roomDataModel?.getProperty(`/${bookingData.seat?.room?.id}`);

            if (roomData) {
                bookingData.seat.room = {
                    id: roomData.id,
                    name: roomData.name || roomData.id,
                    description: roomData.description || null,
                    floorPlan: {
                        id: roomData.floorPlan?.id,
                        name: roomData.floorPlan?.name || "Floor",
                        location: {
                            id: roomData.floorPlan?.location?.id,
                            name: roomData.floorPlan?.location?.name || "Location"
                        }
                    }
                };
            } else if (bookingData.roomName && bookingData.seat?.room) {
                // Ensure roomName is set from available data
                bookingData.seat.room.name = bookingData.roomName;
            }
        }
    }

    public async fetchSelectedSeatData(seatInfo: SeatInfo): Promise<void> {
        const apiService = this.getApiService();
        const seatId = seatInfo.id as string;

        // Set busy state to true when starting data fetch
        if (this.selectedSeatModel) {
            this.selectedSeatModel.setProperty("/isLoading", true);
        }

        try {
            let selected: SelectedSeatData | null = null;
            let allBookings: any[] = [];

            if (seatInfo.isBooked) {
                // Get all bookings for this seat for the selected day
                allBookings = this.getAllBookingsForSeat(seatId);

                // Get the primary booking data (for backward compatibility)
                const cachedBookingData = this.getBookingData(seatId);

                if (cachedBookingData) {
                    selected = cachedBookingData as SelectedSeatData;
                    // Add canCancel property to determine if current user can cancel this booking
                    this.addCanCancelProperty(selected);

                    // Enhance booking data with room details if available
                    const seatDataModel = this.getView().getModel("SeatDataMap") as JSONModel;
                    const seatData = seatDataModel?.getProperty(`/${seatId}`);
                    if (seatData) {
                        this.enhanceBookingDataWithRoomDetails(selected, seatData);
                    }
                } else {
                    Log.warning("No primary booking found for seat:", seatId);
                    // Set busy state to false before returning
                    if (this.selectedSeatModel) {
                        this.selectedSeatModel.setProperty("/isLoading", false);
                    }
                    return;
                }

                // Process all bookings and add canCancel property to each
                allBookings = allBookings.map((booking: any) => {
                    const processedBooking = { ...booking };
                    this.addCanCancelProperty(processedBooking);
                    return processedBooking;
                });
            } else {
                selected = await apiService.seat.fetchById(seatId);
                allBookings = []; // No bookings for unbooked seat
            }

            // Set the data with both selected booking and all bookings
            const modelData = {
                selected,
                allBookings,
                isLoading: false
            };
            this.selectedSeatModel.setData(modelData);
            if (!this.getView().getModel("SelectedSeatModel")) {
                this.getView().setModel(this.selectedSeatModel, "SelectedSeatModel");
            }
        } catch (error) {
            Log.error("Error fetching seat data:", error as Error);
            // Set busy state to false on error
            if (this.selectedSeatModel) {
                this.selectedSeatModel.setProperty("/isLoading", false);
            }
        }
    }

    public async fetchSelectedRoomData(roomInfo: RoomInfo): Promise<void> {
        const apiService = this.getApiService();
        const roomId = roomInfo.id as string;

        // Set busy state to true when starting data fetch
        if (this.selectedSeatModel) {
            this.selectedSeatModel.setProperty("/isLoading", true);
        }

        if (roomInfo.isBooked) {
            try {
                // it should already contain room bookings
                let bookings: any[] = [];

                if (this._cachedBookings?.seats) {
                    // Find bookings for this specific room from cached data
                    const roomBookings = this._cachedBookings.seats.flatMap((seat: any) => {
                        if (seat.room?.id !== roomId) {
                            return [];
                        }
                        return seat.bookedTimes || [];
                    });
                    bookings = roomBookings;
                }
                if (bookings && bookings.length > 0) {
                    // Filter out cancelled bookings and get the active booking
                    const activeBookings = bookings.filter((booking: any) => !booking.cancelled);
                    if (activeBookings.length > 0) {
                        const selected = activeBookings[0];
                        // Add canCancel property to determine if current user can cancel this booking
                        this.addCanCancelProperty(selected);
                        this.selectedSeatModel.setData({ selected, isLoading: false });
                    } else {
                        Log.warning(
                            "No active booking found for room (all bookings are cancelled):",
                            roomId
                        );
                        this.selectedSeatModel.setData({ selected: null, isLoading: false });
                    }
                } else {
                    this.selectedSeatModel.setData({ selected: null, isLoading: false });
                }
                if (!this.getView().getModel("SelectedSeatModel")) {
                    this.getView().setModel(this.selectedSeatModel, "SelectedSeatModel");
                }
            } catch (error) {
                Log.error("Error fetching room bookings:", error as Error);
                // Set busy state to false on error
                if (this.selectedSeatModel) {
                    this.selectedSeatModel.setProperty("/isLoading", false);
                }
            }
        } else {
            try {
                const selected = await apiService.room.fetchById(roomId);
                this.selectedSeatModel.setData({ selected, isLoading: false });
                if (!this.getView().getModel("SelectedSeatModel")) {
                    this.getView().setModel(this.selectedSeatModel, "SelectedSeatModel");
                }
            } catch (error) {
                Log.error("Error fetching floor plans: ", error as Error);
                // Set busy state to false on error
                if (this.selectedSeatModel) {
                    this.selectedSeatModel.setProperty("/isLoading", false);
                }
            }
        }
    }

    public async fetchMyBookings(): Promise<void> {
        const apiService = this.getApiService();
        try {
            const mybookings = await apiService.booking.fetchMyBookings();
            this.myBookingsModel = new JSONModel({ mybookings });
            this.getView().setModel(this.myBookingsModel, "MyBookingsModel");
            // Note: Seat coloring is now handled by calling context (e.g., onDatePickerGraphicChange)
        } catch (error) {
            UIFeedbackHelper.showErrorMessage(
                `Error fetching myBookings: ${(error as Error).message}`
            );
        }
    }

    public async fetchAllEmployees(): Promise<void> {
        if (this._cachedEmployees) {
            this.employeeModel = new JSONModel({ employees: this._cachedEmployees });
            this.getView().setModel(this.employeeModel, "EmployeeModel");
            return;
        }

        // If currently loading, wait for it
        if (this._isLoadingEmployees) {
            while (this._isLoadingEmployees) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            if (this._cachedEmployees) {
                this.employeeModel = new JSONModel({ employees: this._cachedEmployees });
                this.getView().setModel(this.employeeModel, "EmployeeModel");
            }
            return;
        }

        const apiService = this.getApiService();

        try {
            this._isLoadingEmployees = true;
            const employees = await apiService.employee.fetchAllEmployees();

            this._cachedEmployees = employees;

            this.employeeModel = new JSONModel({ employees });
            this.getView().setModel(this.employeeModel, "EmployeeModel");
        } catch (error) {
            Log.error("Error fetching Employees plans:", error as Error);
        } finally {
            this._isLoadingEmployees = false;
        }
    }

    /**
     * Cached version of fetchBookingsForOthers to prevent duplicate API calls
     */
    public async getCachedBookingsForOthers(): Promise<any[]> {
        if (this._cachedBookingsForOthers) {
            return this._cachedBookingsForOthers;
        }

        if (this._isLoadingBookingsForOthers) {
            while (this._isLoadingBookingsForOthers) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            return this._cachedBookingsForOthers || [];
        }

        const apiService = this.getApiService();

        try {
            this._isLoadingBookingsForOthers = true;
            const bookingsForOthers = await apiService.booking.fetchBookingsForOthers();

            this._cachedBookingsForOthers = bookingsForOthers;

            return bookingsForOthers;
        } catch (error) {
            Log.error("Error fetching bookings for others:", error as Error);
            return [];
        } finally {
            this._isLoadingBookingsForOthers = false;
        }
    }

    /**
     * Clear booking-related caches when bookings change
     */
    private clearBookingCaches(): void {
        this._cachedBookings = null;
        this._cachedMyBookings = null;
        this._cachedBookingsForOthers = null;
        this._currentBookingTimespan = null;
        this._cachedRoomBookings = {};

        // Clear the performance optimization cache as well
        this._seatAvailabilityCache.clear();
    }

    public async fetchAllFloors(): Promise<void> {
        if (this._cachedFloorplans) {
            return;
        }

        if (this._isLoadingFloorplans) {
            while (this._isLoadingFloorplans) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            return;
        }

        try {
            this._isLoadingFloorplans = true;
            const apiService = this.getApiService();
            const floorplan = await apiService.floorplan.fetchAll();
            this._cachedFloorplans = floorplan;

            this.floorplansModel = new JSONModel({ floorplan });
            this.getView().setModel(this.floorplansModel, "FloorplanModel");

            // ✅ Default to a specific floorplan by ID
            const defaultFloorId = "55cad9da-8795-4f25-b84b-78dca8b5c85c"; // Erdgeschoss
            const oComboBox = this.byId("idGraphicFloorSelect") as ComboBox;
            if (oComboBox) {
                oComboBox.setSelectedKey(defaultFloorId);
            }
        } catch (error) {
            UIFeedbackHelper.showErrorMessage(
                `Error fetching floorplans: ${(error as Error).message}`
            );
        } finally {
            this._isLoadingFloorplans = false;
        }
    }

    public async markUnauthorizedSeats(): Promise<void> {
        try {
            const seatDataModel = this.getView().getModel("SeatDataMap") as JSONModel;
            const seatsMap = seatDataModel?.getData?.() || {};
            const allSeats: any[] = Object.values(seatsMap);

            // Use the utility to get unauthorized seats
            const { unauthorizedSeats, unauthorizedSeatIds } =
                await HomeZoneAuthUtil.identifyUnauthorizedSeats(allSeats);

            const unauthorizedSeatIdsSet = new Set(unauthorizedSeatIds);

            // Create model and attach to the view
            const unauthorizedSeatsModel = new JSONModel({
                unauthorizedSeats,
                unauthorizedSeatIds
            });
            this.getView().setModel(unauthorizedSeatsModel, "UnauthorizedSeatsModel");

            // Mark shapes for later coloring
            if (this._layer) {
                const rects = this._layer.find("Rect") as Konva.Rect[];
                rects.forEach((shape) => {
                    const sid = shape.getAttr("id");
                    if (!sid) {
                        return;
                    }
                    const isUnauthorized = unauthorizedSeatIdsSet.has(sid);
                    shape.setAttr("isUnauthorized", isUnauthorized);
                });
            }
        } catch (error) {
            Log.error("Error marking unauthorized seats:", error as Error);
            // Ensure at least an empty model is set to avoid binding errors
            const unauthorizedSeatsModel = new JSONModel({
                unauthorizedSeats: [],
                unauthorizedSeatIds: []
            });
            this.getView().setModel(unauthorizedSeatsModel, "UnauthorizedSeatsModel");
        }
    }

    private toggleIcon(shape: Konva.Rect, show: string): void {
        const group = shape.getParent() as Konva.Group;
        if (!group) {
            return;
        }

        let iconType: string;
        switch (show) {
            case "dog": {
                iconType = "🐶";
                break;
            }
            case "child": {
                iconType = "👶";
                break;
            }
            default: {
                console.error("Unknown icon type:", show);
                return;
            }
        }

        let icon = group.findOne(`.${show}`) as Konva.Text;

        if (!icon) {
            icon = new Konva.Text({
                name: show,
                text: iconType,
                fontSize: 10,
                fontFamily: "Arial, sans-serif",
                fill: "white",
                listening: false,
                x: shape.width() / 2 - 5, // Center horizontally
                y: 1 // Top of the seat with small padding
            });
            group.add(icon);
        }
        icon.show();
    }

    private checkIcon(shape: Konva.Rect): void {
        const hasChildBooking = shape.getAttr("isChildBooked");
        const hasDogBooking = shape.getAttr("isDogBooked");

        shape
            .getParent()
            .getChildren()
            .forEach((child) => {
                if (child.attrs.name === "child" || child.attrs.name === "dog") {
                    child.destroy();
                }
            });

        if (hasChildBooking) {
            this.toggleIcon(shape, "child");
        }
        if (hasDogBooking) {
            this.toggleIcon(shape, "dog");
        }
    }

    public async markBookedSeats(): Promise<void> {
        // Ensure unauthorized seats are marked before coloring
        await this.markUnauthorizedSeats();

        // Check if BookedSeatsModel is available, if not, just color all seats as available
        if (!this.BookedSeatsModel) {
            Log.info("BookedSeatsModel not available, showing all seats as available");
            const deskrects = this._layer.find("Rect") as Konva.Rect[];
            deskrects.forEach((shape) => {
                const localSeatId = shape.getAttr("id");
                if (localSeatId && shape.name() === "seat") {
                    // Reset attributes
                    shape.setAttr("isBooked", false);
                    shape.setAttr("isSelfBooked", false);
                    shape.setAttr("isPartiallyBooked", false);
                    shape.setAttr("isFullyBooked", false);
                    shape.setAttr("isChildBooked", false);
                    shape.setAttr("isDogBooked", false);

                    // Color based on authorization only
                    if (shape.getAttr("isUnauthorized")) {
                        shape.fill("grey");
                    } else {
                        shape.fill("green");
                    }
                }
            });
            this._layer.batchDraw();
            return;
        }

        const bookedSeatsData = this.BookedSeatsModel?.getData()?.bookedSeats?.seats || [];
        if (!Array.isArray(bookedSeatsData)) {
            Log.error("Booked seats is not an array");
            return;
        }

        const bookedSeatIds = new Set(bookedSeatsData.map((seat: any) => seat.seatId));

        // Filter my bookings for the currently selected date and exclude cancelled bookings
        const myBookingsData = this.myBookingsModel?.getData()?.mybookings || [];

        // Filter by date and exclude cancelled bookings
        let myFilteredBookings: any[];
        if (this.oDate) {
            const selectedDate = this.oDate; // currently selected date
            myFilteredBookings = myBookingsData.filter((booking: any) => {
                const bookingDate = new Date(booking.startTime);
                return (
                    bookingDate.toDateString() === selectedDate.toDateString() && !booking.cancelled
                );
            });
        } else {
            // If no date selected, still filter out cancelled bookings
            myFilteredBookings = myBookingsData.filter((booking: any) => !booking.cancelled);
        }

        const bookedSelfSeatIds = new Set(
            myFilteredBookings.map((booking: any) => booking.seat.id)
        );

        const childBookings = new Set(
            bookedSeatsData
                .filter((seat: any) =>
                    seat.bookedTimes.some(
                        (time: any) => time.bookingAttributes?.AmountChildren === 1
                    )
                )
                .map((seat: any) => seat.seatId)
        );

        const dogBookings = new Set(
            bookedSeatsData
                .filter((seat: any) =>
                    seat.bookedTimes.some((time: any) => time.bookingAttributes?.AmountDogs === 1)
                )
                .map((seat: any) => seat.seatId)
        );

        // Get all desk shapes
        const deskrects = this._layer.find("Rect") as Konva.Rect[];

        deskrects.forEach((shape) => {
            const localSeatId = shape.getAttr("id"); // seat id of the rectangle

            // Reset to default state first
            if (localSeatId) {
                shape.setAttr("isBooked", false);
                shape.setAttr("isSelfBooked", false);
                shape.setAttr("isPartiallyBooked", false);
                shape.setAttr("isFullyBooked", false);
                shape.setAttr("isChildBooked", false);
                shape.setAttr("isDogBooked", false);

                // Determine booking state
                const isBookedBySomeone = bookedSeatIds.has(localSeatId);
                const isSelfBooked = bookedSelfSeatIds.has(localSeatId) && isBookedBySomeone;
                const isChildBooked = childBookings.has(localSeatId) && isBookedBySomeone;
                const isDogBooked = dogBookings.has(localSeatId) && isBookedBySomeone;
                shape.setAttr("isBooked", isBookedBySomeone);
                shape.setAttr("isSelfBooked", isSelfBooked);
                shape.setAttr("isChildBooked", isChildBooked);
                shape.setAttr("isDogBooked", isDogBooked);

                // Use pre-calculated availability data for fast performance
                const seatData = this.getPreCalculatedSeatData(localSeatId);

                if (shape.getAttr("isUnauthorized")) {
                    // Unauthorized seats remain grey
                    shape.fill("grey");
                } else if (isSelfBooked) {
                    // Self-booked seats are blue for user recognition
                    shape.fill(this.isDarkMode() ? "#002794" : "#1e52e2");
                } else if (seatData) {
                    // We have pre-calculated data, use it for coloring
                    if (seatData.isFullyBooked) {
                        // Fully booked for the whole day
                        shape.fill("#E63946");
                        shape.setAttr("isFullyBooked", true);
                    } else if (seatData.isPartial) {
                        // Has bookings but still has available time slots
                        shape.fill("#E6B800");
                        shape.setAttr("isPartiallyBooked", true);
                    } else if (isBookedBySomeone) {
                        // Seat has bookings but all slots are available (edge case)
                        shape.fill("#34A853");
                    } else {
                        // Available (green)
                        shape.fill("#34A853");
                    }
                } else if (isBookedBySomeone) {
                    console.warn(
                        `Seat ${localSeatId} is booked but has no availability data, defaulting to red`
                    );
                    shape.fill("#E63946");
                } else {
                    // Available (green)
                    shape.fill("#34A853");
                }
                this.checkIcon(shape);
            }
        });

        this._layer.batchDraw();
    }

    /**
     * Fast lookup for pre-calculated seat availability data
     * @param seatId The seat ID to lookup
     * @returns Pre-calculated availability data or null
     */
    private getPreCalculatedSeatData(seatId: string): {
        morning: boolean;
        afternoon: boolean;
        hasBooking: boolean;
        isPartial: boolean;
        isFullyBooked: boolean;
    } | null {
        // First try the cache for super fast lookup
        const cached = this._seatAvailabilityCache?.get(seatId);
        if (cached) {
            const selectedDate = this.oDate || new Date();
            const isToday = getISODateString(selectedDate) === getISODateString(new Date());

            let isFullyBooked: boolean;
            if (isToday) {
                // For today, check if seat is unavailable from current time onwards
                // This requires looking at actual booking data since cache only has morning/afternoon flags
                const bookedSeatsData = this.BookedSeatsModel?.getData()?.bookedSeats?.seats || [];
                const seatData = bookedSeatsData.find((seat: any) => seat.seatId === seatId);

                if (seatData?._availability) {
                    isFullyBooked = seatData._availability.isFullyBooked;
                } else {
                    // Fallback to morning/afternoon logic
                    isFullyBooked = !cached.morning && !cached.afternoon && cached.hasBooking;
                }
            } else {
                // For other days, use morning/afternoon logic
                isFullyBooked = !cached.morning && !cached.afternoon && cached.hasBooking;
            }

            const result = {
                ...cached,
                isPartial:
                    (cached.morning || cached.afternoon) && cached.hasBooking && !isFullyBooked,
                isFullyBooked
            };

            return result;
        }

        // Fallback to processed seat data if cache is not available
        const bookedSeatsData = this.BookedSeatsModel?.getData()?.bookedSeats?.seats || [];
        const seatData = bookedSeatsData.find((seat: any) => seat.seatId === seatId);

        if (seatData?._availability) {
            return seatData._availability;
        }

        return null;
    }

    private async loadFloorplan(): Promise<void> {
        // Try to get a selected floor from the new LocationFloor ComboBox first
        let selectedFloorId: string;
        const oLocationFloorCombo = this.byId("idItemsLocationFloorComboBox") as ComboBox;
        if (oLocationFloorCombo?.getSelectedKey()) {
            selectedFloorId = oLocationFloorCombo.getSelectedKey();
        } else {
            // Fall back to the old floor select
            const oCombo = this.byId("idGraphicFloorSelect") as ComboBox;
            selectedFloorId = oCombo?.getSelectedKey() || "";
        }

        if (!selectedFloorId) {
            Log.warning("No floor selected");
            return;
        }

        await this.fetchAllFloors();
        const floorplansModel = this.getView().getModel("FloorplanModel") as JSONModel;
        const data = floorplansModel.getData();
        const floorplans = data.floorplan || [];

        const selectedFloorplan = floorplans.find(
            (fp: { id: string }) => fp.id === selectedFloorId
        );

        if (!selectedFloorplan) {
            Log.warning(`No floorplan found for id: ${selectedFloorId}`);
            return;
        }

        // In backend response, "svg" holds the file reference (could be null in your example)
        const floorplanUrl = selectedFloorplan.svg;

        if (!floorplanUrl || null) {
            Log.warning(`No SVG URL found for floorplan: ${selectedFloorplan.name}`);
            return;
        }

        const canvasElement = document.getElementById(this.canvasElementId);
        if (canvasElement) {
            await this.createFloorplanImage(selectedFloorplan.id);
        }
    }

    public async onFloorSelectionChange(): Promise<void> {
        // Clear existing floorplan
        this._clearFloorplan();

        // Get the selected floor from the location-floor combobox
        const oCombo = this.byId("idItemsLocationFloorComboBox") as ComboBox;
        const selectedKey = oCombo?.getSelectedKey();

        if (selectedKey) {
            const oFloorSelect = this.byId("idGraphicFloorSelect") as Select;
            if (oFloorSelect) {
                oFloorSelect.setSelectedKey(selectedKey);
            }
        }

        await this.loadFloorplan();
    }

    private createRoomsFromJSON(floorId: string): void {
        const roomsData = this.getView().getModel("RoomData")?.getProperty(`/${floorId}`);
        if (!roomsData) {
            Log.error(`No room data found for floorId: ${floorId}`);
            return;
        }

        roomsData.forEach((room: KonvaRoom) => {
            const roomGroup = this.createRoom(room);
            if (this._contentGroup) {
                this._contentGroup.add(roomGroup);
            } else {
                this._layer.add(roomGroup);
            }
        });
        this._layer.draw();
        this._applyDesignScale();

        // Note: Seat coloring is now handled by the calling context (e.g., onDatePickerGraphicChange)
    }

    private createRoom(roomData: KonvaRoom): Konva.Group {
        const roomGroup = new Konva.Group({ x: roomData.x, y: roomData.y });

        // Use theme-aware stroke color for walls
        const strokeColor = this.isDarkMode() ? "white" : roomData.borderColor || "black";

        const boundary = new Konva.Rect({
            width: roomData.width,
            height: roomData.height,
            stroke: strokeColor,
            strokeWidth: 0
        });
        roomGroup.add(boundary);

        const roomLabel = new Konva.Text({
            x: 25,
            y: roomData.height - 10,
            text: roomData.name || "Room",
            fontSize: 14,
            fontFamily: "Arial",
            fill: this.isDarkMode() ? "white" : "black",
            align: "center",
            verticalAlign: "middle"
        });

        roomLabel.offsetX(roomLabel.width() / 2);
        roomLabel.offsetY(roomLabel.height() / 2);

        roomGroup.add(roomLabel);

        if (this.isConferenceRoom(roomData.id)) {
            roomGroup.setAttr("isBooked", false);
            roomGroup.setAttr("type", "meetingroom");

            // Use visible room background color for both light and dark themes
            boundary.fill("rgba(173, 216, 230, 0.3)");

            boundary.on("pointerdown", () => {
                // Hide tooltip when room is clicked
                this.hideTooltip();
                this.handleRoomSelection(roomData, boundary);
            });
            boundary.on("pointerover", (e) => {
                // Add corner indicators instead of changing stroke color
                this.addCornerIndicators(boundary, roomGroup);
                this._layer.draw();

                // Show tooltip with conference room information using screen coordinates
                const tooltipText = this.formatConferenceRoomTooltip(roomData, boundary);
                const containerRect = this._stage.container().getBoundingClientRect();
                const pointerPos = this._stage.getPointerPosition();
                if (pointerPos) {
                    const screenX = containerRect.left + pointerPos.x;
                    const screenY = containerRect.top + pointerPos.y;
                    this.showTooltip(screenX, screenY, tooltipText);
                }
            });
            boundary.on("pointerout", () => {
                // Hide tooltip
                this.hideTooltip();

                // Remove corner indicators
                this.removeCornerIndicators(roomGroup);
                this._layer.draw();
            });
            boundary.on("pointermove", (e) => {
                // Update tooltip position as mouse moves over the conference room using screen coordinates
                const containerRect = this._stage.container().getBoundingClientRect();
                const pointerPos = this._stage.getPointerPosition();
                if (
                    pointerPos &&
                    this._domTooltip &&
                    this._domTooltip.style.visibility === "visible"
                ) {
                    const screenX = containerRect.left + pointerPos.x;
                    const screenY = containerRect.top + pointerPos.y;
                    this._domTooltip.style.left = screenX + 15 + "px";
                    this._domTooltip.style.top = screenY - 10 + "px";
                }
            });
        }

        // Always create seats regardless of whether it's a conference room or not
        roomData.seats.forEach((seatData: KonvaSeat) => {
            const seat = this.createSeat(seatData);
            roomGroup.add(seat);
        });
        return roomGroup;
    }

    private createSeat(seatData: KonvaSeat): Konva.Group {
        // Use white stroke for seats in dark mode for better visibility
        const seatStrokeColor = this.isDarkMode() ? "white" : "black";
        const isHorizontal = seatData.orientation === "HORIZONTAL";
        const seatWidth = isHorizontal ? 30 : 11;
        const seatHeight = isHorizontal ? 11 : 30;

        // Create a group to hold both the seat rectangle and the text label
        const seatGroup = new Konva.Group({
            x: seatData.x,
            y: seatData.y,
            // Hide "ALL" seats from the graphic display
            visible: seatData.label !== "ALL"
        });

        const seat = new Konva.Rect({
            id: seatData.id,
            name: "seat",
            width: seatWidth,
            height: seatHeight,
            fill: "transparent",
            stroke: seatStrokeColor,
            strokeWidth: 1,
            cornerRadius: 3, // Keep rounded corners
            x: 0,
            y: 0
        });

        // Store the label as an attribute for search functionality
        seat.setAttr("label", seatData.label);
        seat.setAttr("isBooked", false);
        seat.setAttr("isSelfBooked", false);
        seat.setAttr("type", "seat");

        // Split by "-" and take the last part (e.g., "ROOM-A-12" -> "12")
        const displayLabel = seatData.label.split("-").pop() || seatData.label;
        const seatText = new Konva.Text({
            text: displayLabel,
            fontSize: isHorizontal ? 7 : 6,
            fontFamily: "Roboto, sans-serif",
            fill: "white",
            width: seatWidth,
            height: seatHeight,
            align: "center",
            verticalAlign: "middle",
            listening: false, // Text should not capture events
            x: 0,
            y: 0
        });

        // Add both shapes to the group
        seatGroup.add(seat);
        seatGroup.add(seatText);

        // Store reference to text for color updates
        seat.setAttr("textLabel", seatText);

        seatGroup.on("pointerdown", () => {
            // Hide tooltip when seat is clicked
            this.hideTooltip();
            this.handleSeatSelection(seatData, seat);
        });
        seatGroup.on("pointerover", (e) => {
            // Add corner indicators instead of changing fill color
            this.addCornerIndicators(seat, seatGroup);
            this._layer.draw();

            // Show tooltip with seat information using screen coordinates
            const tooltipText = this.formatSeatTooltip(seatData, seat);
            const containerRect = this._stage.container().getBoundingClientRect();
            const pointerPos = this._stage.getPointerPosition();
            if (pointerPos) {
                const screenX = containerRect.left + pointerPos.x;
                const screenY = containerRect.top + pointerPos.y;
                this.showTooltip(screenX, screenY, tooltipText);
            }
        });
        seatGroup.on("pointerout", () => {
            // Hide tooltip
            this.hideTooltip();

            // Remove corner indicators
            this.removeCornerIndicators(seatGroup);
            this._layer.draw();
        });
        seatGroup.on("pointermove", (e) => {
            // Update tooltip position as mouse moves over the seat using screen coordinates
            const containerRect = this._stage.container().getBoundingClientRect();
            const pointerPos = this._stage.getPointerPosition();
            if (pointerPos && this._domTooltip && this._domTooltip.style.visibility === "visible") {
                const screenX = containerRect.left + pointerPos.x;
                const screenY = containerRect.top + pointerPos.y;
                this._domTooltip.style.left = screenX + 15 + "px";
                this._domTooltip.style.top = screenY - 10 + "px";
            }
        });
        return seatGroup;
    }

    private handleRoomSelection(roomData: KonvaRoom, roomShape: Konva.Rect): void {
        // Prevent selection during pinch zoom or stage dragging
        if (this._stage.isDragging() || this._isPinchZooming) {
            return;
        }

        // Hide tooltip immediately when room is clicked and force cleanup
        this.hideTooltip();

        // Force hide any lingering tooltips by ensuring DOM cleanup
        if (this._domTooltip) {
            this._domTooltip.style.visibility = "hidden";
            this._domTooltip.style.opacity = "0";
        }

        // eslint-disable-next-line fiori-custom/sap-timeout-usage
        setTimeout(async () => {
            // Double-check both drag and pinch zoom state after timeout
            if (this._stage.isDragging() || this._isPinchZooming) {
                return;
            }

            const isBooked = roomShape.getAttr("isBooked") || false;
            const roomInfo = { ...roomData, isBooked };
            const isConferenceRoom = this.isConferenceRoom(roomData.id);

            // Initialize the model with loading state BEFORE opening dialog for booked rooms
            if (isBooked) {
                this.selectedSeatModel = new JSONModel({ selected: null, isLoading: true });
                this.getView().setModel(this.selectedSeatModel, "SelectedSeatModel");
            }

            if (!isBooked && isConferenceRoom) {
                // For unbooked conference rooms, set up room-specific dialog data
                this.setDialogDataFromRoom(roomData); // Remove await to prevent blocking
            } else {
                // For regular rooms or booked rooms, use existing flow
                this.fetchSelectedRoomData(roomInfo).catch((error) => {
                    Log.error("Error fetchting Room data:", error as Error);
                });
            }

            // Use async function for openSeatDialog call
            await this.openSeatDialog(isBooked as boolean);
        }, 100);
    }

    private handleSeatSelection(seatData: KonvaSeat, seatShape: Konva.Rect): void {
        // Hide tooltip immediately when seat is clicked and force cleanup
        this.hideTooltip();

        // Force hide any lingering tooltips by ensuring DOM cleanup
        if (this._domTooltip) {
            this._domTooltip.style.visibility = "hidden";
            this._domTooltip.style.opacity = "0";
        }

        // eslint-disable-next-line fiori-custom/sap-timeout-usage
        setTimeout(() => {
            // Prevent selection during pinch zoom or stage dragging
            if (this._stage.isDragging() || this._isPinchZooming) {
                return;
            }

            const isBooked = seatShape.getAttr("isBooked") || false;
            const isUnauthorized = seatShape.getAttr("isUnauthorized") || false;
            const isPartiallyBooked = seatShape.getAttr("isPartiallyBooked") || false;

            // Disable selection for unauthorized free seats
            if (!isBooked && isUnauthorized) {
                return;
            }

            // Get the full seat data with room details from the stored model
            const seatDataModel = this.getView().getModel("SeatDataMap") as JSONModel;
            const fullSeatData = seatDataModel?.getProperty(`/${seatData.id}`) || seatData;

            // Determine dialog type based on seat status:
            // - Green seats (available): Show booking dialog
            // - Yellow seats (partially booked): Show booking dialog (can book remaining slots)
            // - Red seats (fully booked): Show booked seat details dialog
            const shouldShowBookingDialog = !isBooked || isPartiallyBooked;

            if (shouldShowBookingDialog) {
                // For available seats or partially booked seats, show booking dialog
                const seatInfo = { ...fullSeatData, isBooked: false }; // Always treat as bookable for dialog

                // Get existing bookings for this seat to filter available time slots
                const existingBookings = this.getAllBookingsForSeat(seatData.id);
                seatInfo.existingBookings = existingBookings;

                this.setDialogDataFromSeat(seatInfo);
            } else {
                // For fully booked seats, show booking details (existing behavior)
                const cachedBookingData = this.getBookingData(seatData.id);

                if (cachedBookingData) {
                    // Enhance the booking data with room details from cached room data
                    this.enhanceBookingDataWithRoomDetails(cachedBookingData, fullSeatData);

                    // Add canCancel property to determine if current user can cancel this booking
                    this.addCanCancelProperty(cachedBookingData);

                    // Get all bookings for this seat
                    const allBookings = this.getAllBookingsForSeat(seatData.id);

                    // Create the model with the cached data including all bookings
                    const modelData = {
                        selected: cachedBookingData,
                        allBookings: allBookings,
                        isLoading: false
                    };

                    this.selectedSeatModel = new JSONModel(modelData);
                    this.getView().setModel(this.selectedSeatModel, "SelectedSeatModel");
                } else {
                    // Fallback to API call if no cached data found (shouldn't happen for properly booked seats)
                    Log.warning(
                        "No cached booking data found for seat, falling back to API call:",
                        seatData.id
                    );
                    const seatInfo = { ...fullSeatData, isBooked };

                    this.selectedSeatModel = new JSONModel({ selected: null, isLoading: true });
                    this.getView().setModel(this.selectedSeatModel, "SelectedSeatModel");

                    this.fetchSelectedSeatData(seatInfo).catch((error) => {
                        Log.error("Error fetching seat data:", error as Error);
                    });
                }
            }

            (async () => {
                await this.openSeatDialog(!shouldShowBookingDialog);
            })();
        }, 100);
    }

    private async openSeatDialog(booked: boolean): Promise<void> {
        if (booked) {
            await this.openGraphicBookedDialog();
            return;
        }

        const fragmentName = "de.realcore.realseats.view.fragment.SeatBookingDialog";

        // Load employee data when opening SeatBookingDialog
        await this.loadEmployeeData();

        // Close existing dialog if it exists
        if (this._dialog) {
            this._dialog.close();
            this._dialog.destroy();
            this._dialog = null;
        }

        Fragment.load({
            name: fragmentName,
            controller: this,
            id: this.getView().getId() + "--SeatBooking--" + Date.now()
        })
            .then((oFragment: Control | Control[]) => {
                if (!oFragment) {
                    Log.error("Fragment not loaded");
                    return;
                }

                // Get the Dialog from the fragment
                let dialog: Dialog;
                if (Array.isArray(oFragment)) {
                    dialog = oFragment[0] as Dialog;
                } else {
                    dialog = oFragment as Dialog;
                }

                if (!dialog || !(dialog instanceof Dialog)) {
                    Log.error("Fragment content is not a Dialog");
                    return;
                }

                try {
                    // Store dialog reference
                    this._dialog = dialog;

                    // Add dialog to view
                    this.getView().addDependent(dialog);

                    // Ensure the dialog has the employee model
                    const employeeModel = this.getView().getModel("employees");
                    if (employeeModel) {
                        dialog.setModel(employeeModel, "employees");
                    }

                    // Open dialog
                    dialog.open();
                } catch (error) {
                    Log.error("Error opening dialog:", error as Error);
                }
            })
            .catch((error) => {
                Log.error("Failed to load dialog fragment:", error as Error);
            });
    }

    /**
     * Opens the GraphicBooked dialog as a standalone Dialog
     */
    private async openGraphicBookedDialog(): Promise<void> {
        const fragmentName = "de.realcore.realseats.view.fragment.GraphicBooked";

        Fragment.load({
            name: fragmentName,
            controller: this,
            id: this.getView().getId() + "--GraphicBooked--" + Date.now()
        })
            .then((oFragment: Control | Control[]) => {
                if (!oFragment) {
                    Log.error("GraphicBooked fragment not loaded");
                    return;
                }

                // Ensure we have a single control (the Dialog)
                let dialog: Dialog;
                if (Array.isArray(oFragment)) {
                    dialog = oFragment[0] as Dialog;
                } else {
                    dialog = oFragment as Dialog;
                }

                // Basic validation
                if (!dialog || typeof dialog !== "object") {
                    Log.error(
                        "GraphicBooked fragment is not a valid Dialog: " +
                            (dialog?.toString() || "undefined")
                    );
                    return;
                }

                try {
                    // Add dialog to view and open it
                    this.getView().addDependent(dialog);
                    dialog.open();

                    // Store reference separately from the SeatBookingDialog
                    this._graphicBookedDialog = dialog;
                } catch (error) {
                    Log.error("Error opening GraphicBooked dialog:", error as Error);
                }
            })
            .catch((error) => {
                Log.error("Failed to load GraphicBooked dialog fragment:", error as Error);
            });
    }

    public async onConfirmBooking(): Promise<void> {
        try {
            const DialogDataModel = this.getView().getModel("DialogData") as JSONModel;
            const dialogData = DialogDataModel.getData();

            if (dialogData.isConferenceRoom) {
                // Handle conference room booking
                await this.confirmRoomBooking();
            } else {
                // Handle regular seat booking
                await this.confirmSeatBooking();
            }

            MessageToast.show(this.getText("bookingConfirmedSuccessfully"));
            this._dialog?.close();

            // Refresh the graphic to show the new booking
            await this.FetchBookingsSpaces();
            await this.fetchMyBookings();
            await this.markBookedSeats(); // Update seat colors after booking
        } catch (error) {
            Log.error("Booking error:", error as Error);
            const errorMessage = (error as Error).message || "Unknown booking error occurred";
            MessageToast.show(`Booking failed: ${errorMessage}`);
        }
    }

    private async confirmSeatBooking(): Promise<void> {
        const { dialogData, seatId, startISO, endISO, attributes } = this.prepareBookingData();
        const employeeId = await this.getEmployeeIdForBooking(dialogData);
        await this.createBookingRequest(seatId, startISO, endISO, employeeId, attributes);
    }

    private async confirmRoomBooking(): Promise<void> {
        const DialogDataModel = this.getView().getModel("DialogData") as JSONModel;
        const dialogData = DialogDataModel.getData();

        const selectedDate = dialogData.date;
        const startTime = dialogData.startTime;
        const endTime = dialogData.endTime;
        const roomId = dialogData.roomID;

        const startDateTime = new Date(selectedDate);
        const endDateTime = new Date(selectedDate);

        const [startHour, startMinute] = startTime.split(":").map(Number);
        const [endHour, endMinute] = endTime.split(":").map(Number);

        startDateTime.setHours(startHour, startMinute, 0, 0);
        endDateTime.setHours(endHour, endMinute, 0, 0);

        const startISO = formatter.formatLocalDateTime(startDateTime);
        const endISO = formatter.formatLocalDateTime(endDateTime);

        const employeeId = await this.getEmployeeIdForBooking(dialogData);

        // Find and book the "ALL" seat for this conference room
        await this.bookAllSeatForRoom(roomId, startISO, endISO, employeeId);
    }

    /**
     * Books the "ALL" seat for a conference room (workaround to avoid backend changes)
     */
    private async bookAllSeatForRoom(
        roomId: string,
        startISO: string,
        endISO: string,
        employeeId: string | null
    ): Promise<void> {
        try {
            // Find the "ALL" seat for this room
            const allSeatId = await this.findAllSeatForRoom(roomId);
            if (!allSeatId) {
                throw new Error(`No "ALL" seat found for conference room ${roomId}`);
            }

            // Book the "ALL" seat using the regular seat booking API
            await this.createBookingRequest(allSeatId, startISO, endISO, employeeId);

            Log.info(`Successfully booked "ALL" seat ${allSeatId} for conference room ${roomId}`);
        } catch (error) {
            Log.error("Error booking ALL seat for conference room:", error as Error);
            throw error;
        }
    }

    /**
     * Finds the "ALL" seat ID for a specific room
     */
    private async findAllSeatForRoom(roomId: string): Promise<string | null> {
        try {
            // Get all seats from cached data
            const allSeats = this._cachedSeats || (await this.fetchAllSeats());

            // Find the seat with name "ALL" in the specified room
            const allSeat = allSeats.find(
                (seat: Seat) => seat.room?.id === roomId && seat.name === "ALL"
            );

            if (allSeat) {
                Log.info(`Found "ALL" seat with ID ${allSeat.id} for room ${roomId}`);
                return allSeat.id;
            }

            Log.warning(`No "ALL" seat found for room ${roomId}`);
            return null;
        } catch (error) {
            Log.error("Error finding ALL seat for room:", error as Error);
            return null;
        }
    }

    /**
     * Checks if a conference room is fully booked for a given timespan
     * @param roomId The room ID to check
     * @param startTime The start time in ISO format
     * @param endTime The end time in ISO format
     * @returns Promise<boolean> true if the room is fully booked, false otherwise
     */
    private async isConferenceRoomFullyBooked(
        roomId: string,
        startTime: string,
        endTime: string
    ): Promise<boolean> {
        try {
            // Find the "ALL" seat for this conference room
            const allSeatId = await this.findAllSeatForRoom(roomId);
            if (!allSeatId) {
                // If no "ALL" seat found, assume room is not booked as conference room
                return false;
            }

            const apiService = this.getApiService();

            // Check if the "ALL" seat is booked for the given timespan
            const bookings = await apiService.booking.fetchByIdAndDate(
                { seatId: allSeatId },
                startTime,
                endTime
            );

            // Filter out cancelled bookings and check if any active bookings exist
            const activeBookings = bookings
                ? bookings.filter((booking: any) => !booking.cancelled)
                : [];

            return activeBookings.length > 0;
        } catch (error) {
            Log.error("Error checking if conference room is fully booked:", error as Error);
            // In case of error, assume room is not booked to avoid blocking user
            return false;
        }
    }

    /**
     * Checks conference room booking status for a seat booking dialog
     * @param dialogData The dialog data object to update
     */
    private async checkConferenceRoomBookingStatus(dialogData: any): Promise<void> {
        const roomId = dialogData.roomID;

        // Only check if this is a SEAT booking in a conference room
        // Skip this check for room bookings (isConferenceRoom: true)
        if (!roomId || !this.isConferenceRoom(roomId) || dialogData.isConferenceRoom) {
            return;
        }

        try {
            // Get the time range from dialog data
            let startTime: string;
            let endTime: string;

            if (dialogData.useFlexibleTime) {
                startTime = dialogData.startTime;
                endTime = dialogData.endTime;
            } else {
                // Parse the time range selection
                const timeRange = dialogData.timeRange;
                if (timeRange === TIME_RANGES.MORNING) {
                    startTime = TIME_RANGES.MORNING.split("-")[0];
                    endTime = TIME_RANGES.MORNING.split("-")[1];
                } else if (timeRange === TIME_RANGES.AFTERNOON) {
                    startTime = TIME_RANGES.AFTERNOON.split("-")[0];
                    endTime = TIME_RANGES.AFTERNOON.split("-")[1];
                } else {
                    startTime = TIME_RANGES.FULL_DAY.split("-")[0];
                    endTime = TIME_RANGES.FULL_DAY.split("-")[1];
                }
            }

            // Create full date/time objects for API call
            const selectedDate = dialogData.date;
            const startDateTime = new Date(selectedDate);
            const endDateTime = new Date(selectedDate);

            const [startHour, startMinute] = startTime.split(":").map(Number);
            const [endHour, endMinute] = endTime.split(":").map(Number);

            startDateTime.setHours(startHour, startMinute, 0, 0);
            endDateTime.setHours(endHour, endMinute, 0, 0);

            const startISO = formatter.formatLocalDateTime(startDateTime);
            const endISO = formatter.formatLocalDateTime(endDateTime);

            // Check if the conference room is fully booked
            const isRoomBooked = await this.isConferenceRoomFullyBooked(roomId, startISO, endISO);

            // Update dialog data with room booking status
            const dialogModel = this.getView().getModel("DialogData") as JSONModel;
            if (dialogModel && isRoomBooked) {
                dialogModel.setProperty("/isRoomFullyBooked", true);
                dialogModel.setProperty("/showRoomBookingWarning", true);
                dialogModel.setProperty(
                    "/roomBookingMessage",
                    "You can reserve this seat, but the room is in use during that time."
                );
            } else {
                dialogModel.setProperty("/showRoomBookingWarning", false);
            }
        } catch (error) {
            Log.error("Error checking conference room booking status:", error as Error);
        }
    }

    private async createRoomBookingRequest(
        roomId: string,
        startISO: string,
        endISO: string,
        employeeId: string | null
    ): Promise<void> {
        const apiService = this.getApiService();
        const eventId = "68ef5b6f-40bd-4eea-b970-45dfa6213279"; // default internal Event
        const description = "Conference room booking from Graphic view";

        try {
            // Use the proper room booking API endpoint
            // When employeeId is null (booking for yourself), don't pass employeeIds
            // When employeeIds is provided, it must match the personAmount exactly
            if (employeeId) {
                // Booking for someone else - pass the employee ID
                await apiService.booking.createRoomBooking(
                    roomId,
                    startISO,
                    endISO,
                    eventId,
                    description,
                    1, // personAmount
                    [employeeId] // employeeIds array with one ID
                );
            } else {
                // Booking for yourself - don't pass employeeIds parameter
                await apiService.booking.createRoomBooking(
                    roomId,
                    startISO,
                    endISO,
                    eventId,
                    description,
                    1 // personAmount only, employeeIds will be undefined
                );
            }

            this.clearBookingCaches();
        } catch (error) {
            Log.error("Error creating room booking:", error as Error);
            throw error;
        }
    }

    private prepareBookingData(): {
        dialogData: any;
        seatId: string;
        startISO: string;
        endISO: string;
        attributes?: {
            AMOUNT_CHILDREN?: number;
            AMOUNT_DOGS?: number;
        };
    } {
        const DialogDataModel = this.getView().getModel("DialogData") as JSONModel;
        if (!DialogDataModel) {
            throw new Error(this.getText("noDialogDataForBooking"));
        }
        const attributes: { [key: string]: number } = {};

        const dialogData = DialogDataModel.getData();

        if (dialogData.isConferenceRoom) {
            // For conference rooms, we don't use seat data
            const selectedDate = dialogData.date;
            const startTime = dialogData.startTime;
            const endTime = dialogData.endTime;

            const startDate = new Date(selectedDate);
            const endDate = new Date(selectedDate);

            const [startHour, startMinute] = startTime.split(":").map(Number);
            const [endHour, endMinute] = endTime.split(":").map(Number);

            startDate.setHours(startHour, startMinute, 0, 0);
            endDate.setHours(endHour, endMinute, 0, 0);

            return {
                dialogData,
                seatId: "", // No seat for conference rooms
                startISO: formatter.formatLocalDateTime(startDate),
                endISO: formatter.formatLocalDateTime(endDate)
            };
        } else {
            // Seat booking logic - handle both regular time slots and flexible time
            const selectedDate = dialogData.date;
            const useFlexibleTime = dialogData.useFlexibleTime;
            let startTimeStr: string;
            let endTimeStr: string;

            if (useFlexibleTime) {
                // For flexible time mode, get times from startTime/endTime properties
                startTimeStr = dialogData.startTime || getCurrentTimePlusMinutes(1);
                endTimeStr = dialogData.endTime;
            } else {
                // For regular time slots, parse from timeRange
                const timeRange = dialogData.timeRange;
                if (timeRange.includes("-")) {
                    [startTimeStr, endTimeStr] = timeRange.split("-");
                } else {
                    // Fallback for invalid timeRange
                    startTimeStr = "08:00";
                    endTimeStr = "17:00";
                }
            }

            // Validate and adjust start time if booking is for today and time has passed
            const adjustedStartTime = this.validateAndAdjustStartTime(startTimeStr, selectedDate);

            const startDate = new Date(selectedDate);
            const endDate = new Date(selectedDate);

            startDate.setHours(
                parseInt(adjustedStartTime.split(":")[0]),
                parseInt(adjustedStartTime.split(":")[1]) || 0,
                0,
                0
            );
            endDate.setHours(
                parseInt(endTimeStr.split(":")[0]),
                parseInt(endTimeStr.split(":")[1]) || 0,
                0,
                0
            );

            // Log if time was adjusted
            if (adjustedStartTime !== startTimeStr) {
                console.log(
                    `Start time adjusted from ${startTimeStr} to ${adjustedStartTime} due to current time`
                );
            }
            if (dialogData.bringingChild) {
                attributes.AMOUNT_CHILDREN = 1;
            }
            if (dialogData.bringingDog) {
                attributes.AMOUNT_DOGS = 1;
            }

            return {
                dialogData,
                seatId: dialogData.seatId,
                startISO: formatter.formatLocalDateTime(startDate),
                endISO: formatter.formatLocalDateTime(endDate),
                attributes
            };
        }
    }

    /**
     * Gets the current user's employee ID from existing booking data
     * @returns string The current user's employee ID
     */
    private getCurrentUserEmployeeId(): string | null {
        // Get current user ID from myBookings - all bookings in this model belong to the current user
        const myBookingsData = this.myBookingsModel?.getData()?.mybookings || [];

        if (myBookingsData.length > 0) {
            // Take the employee ID from the first booking - they all belong to the current user
            const currentUserEmployeeId = myBookingsData[0].employee?.id;
            if (currentUserEmployeeId) {
                console.log("Current user employee ID from myBookings:", currentUserEmployeeId);
                return currentUserEmployeeId;
            }
        }

        return null;
    }

    private async getEmployeeIdForBooking(dialogData: any): Promise<string | null> {
        if (!dialogData.forOthers || !dialogData.selectedEmployee) {
            // When booking for yourself, return null as employee ID should not be set
            return null;
        }

        if (this.selectedEmployeeId) {
            return this.selectedEmployeeId;
        }

        const employeesModel = this.getView().getModel("employees") as JSONModel;
        if (!employeesModel) {
            throw new Error("Employee model not available");
        }

        const employees = employeesModel.getProperty("/employees") || [];
        const foundEmployee = employees.find((emp: any) => {
            const fullName = `${emp.lastName}, ${emp.firstName}`;
            return fullName === dialogData.selectedEmployee;
        });

        if (!foundEmployee) {
            throw new Error(`Employee not found: ${dialogData.selectedEmployee}`);
        }

        return foundEmployee.id;
    }

    private async createBookingRequest(
        seatId: string,
        startISO: string,
        endISO: string,
        employeeId: string | null,
        attributes?: { AMOUNT_CHILDREN?: number; AMOUNT_DOGS?: number }
    ): Promise<void> {
        const apiService = this.getApiService();
        const eventId = "68ef5b6f-40bd-4eea-b970-45dfa6213279"; // default internal Event
        const description = "Booking from Graphic view";

        await apiService.booking.createBooking(
            seatId,
            startISO,
            endISO,
            employeeId,
            eventId,
            description,
            attributes
        );

        // Clear booking caches since bookings have changed
        this.clearBookingCaches();
    }

    /**
     * Handle View Existing Bookings button press
     */
    public onViewExistingBookingsButtonPress(): void {
        void this.onViewRoomBookings();
    }

    public onViewExistingBookingsCalendarButtonPress(): void {
        void this.onViewRoomBookingsCalendar();
    }

    /**
     * Handle View Bookings button press for regular seats
     */
    public onViewExistingBookingsButtonRegularPress(): void {
        // Get the current dialog data to retrieve seat information
        const dialogDataModel = this.getView().getModel("DialogData") as JSONModel;
        const dialogData = dialogDataModel?.getData();

        if (!dialogData?.seatId) {
            Log.error("No seat ID found in dialog data");
            return;
        }

        // Get all bookings for this seat
        const allBookings = this.getAllBookingsForSeat(dialogData.seatId);

        if (allBookings.length === 0) {
            Log.warning("No bookings found for seat:", dialogData.seatId);
            return;
        }

        // Use the first booking as the selected booking for the GraphicBooked dialog
        const firstBooking = allBookings[0];

        // Create the model data structure expected by GraphicBooked fragment
        const modelData = {
            selected: firstBooking,
            allBookings: allBookings,
            isLoading: false
        };

        // Set up the model for the GraphicBooked dialog
        this.selectedSeatModel = new JSONModel(modelData);
        this.getView().setModel(this.selectedSeatModel, "SelectedSeatModel");

        // Open the GraphicBooked dialog to show existing bookings
        void this.openGraphicBookedDialog();
    }

    /**
     * Handle viewing existing room bookings
     */
    public async onViewRoomBookings(): Promise<void> {
        const DialogDataModel = this.getView().getModel("DialogData") as JSONModel;
        const dialogData = DialogDataModel.getData();

        if (!dialogData.isConferenceRoom) {
            Log.warning("View room bookings is only available for conference rooms");
            return;
        }

        const roomId = dialogData.roomID;
        const selectedDate = dialogData.date;

        // Initialize model with loading state and open dialog immediately
        const existingBookingsModel = new JSONModel({
            roomName: dialogData.roomName || roomId,
            selectedDate: selectedDate.toLocaleDateString(),
            bookings: [],
            hasBookings: false,
            isLoading: true
        });

        this.getView().setModel(existingBookingsModel, "ExistingRoomBookings");

        // Open the dialog immediately with loading state
        await this.openExistingBookingsDialog();

        // Now load the data in the background
        try {
            // Get start and end of the selected day
            const startOfDay = new Date(selectedDate);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(selectedDate);
            endOfDay.setHours(23, 59, 59, 999);

            const startISO = formatter.formatLocalDateTime(startOfDay);
            const endISO = formatter.formatLocalDateTime(endOfDay);

            const apiService = this.getApiService();

            // Find the "ALL" seat for this room to get conference room bookings only
            const allSeatId = await this.findAllSeatForRoom(roomId);
            if (!allSeatId) {
                // If no "ALL" seat found, update model with empty data
                existingBookingsModel.setData({
                    roomName: dialogData.roomName || roomId,
                    selectedDate: selectedDate.toLocaleDateString(),
                    bookings: [],
                    hasBookings: false,
                    isLoading: false
                });
                return;
            }

            // Fetch existing bookings specifically for the "ALL" seat (conference room bookings)
            const existingBookings = await apiService.booking.fetchByIdAndDate(
                { seatId: allSeatId },
                startISO,
                endISO
            );

            if (existingBookings && existingBookings.length > 0) {
                existingBookings.forEach((booking: any, index: number) => {
                    console.log(`Booking ${index + 1}:`, {
                        id: booking.id,
                        startTime: booking.startTime,
                        endTime: booking.endTime,
                        cancelled: booking.cancelled,
                        employeeName:
                            `${booking.employee?.firstName || ""} ${booking.employee?.lastName || ""}`.trim(),
                        bookedBy:
                            `${booking.bookingUser?.firstName || ""} ${booking.bookingUser?.lastName || ""}`.trim()
                    });
                });
            }

            // Filter out cancelled bookings and format for display
            const activeBookings = existingBookings
                ? existingBookings.filter((booking: any) => !booking.cancelled)
                : [];

            // Format bookings for display
            const formattedBookings = activeBookings.map((booking: any) => {
                const startTime = new Date(booking.startTime);
                const endTime = new Date(booking.endTime);

                return {
                    id: booking.id,
                    startTime: startTime.toLocaleTimeString("de-DE", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false
                    }),
                    endTime: endTime.toLocaleTimeString("de-DE", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false
                    }),
                    employeeName:
                        `${booking.employee?.firstName || ""} ${booking.employee?.lastName || ""}`.trim(),
                    department: booking.employee?.department?.description || "N/A",
                    description: booking.description || "Conference room booking",
                    bookedBy:
                        `${booking.bookingUser?.firstName || ""} ${booking.bookingUser?.lastName || ""}`.trim()
                };
            });

            // Update model with loaded data
            existingBookingsModel.setData({
                roomName: dialogData.roomName || roomId,
                selectedDate: selectedDate.toLocaleDateString(),
                bookings: formattedBookings,
                hasBookings: formattedBookings.length > 0,
                isLoading: false
            });
        } catch (error) {
            Log.error("Error fetching existing room bookings:", error as Error);
            MessageToast.show("Failed to load existing room bookings");

            // Update model to show error state
            existingBookingsModel.setData({
                roomName: dialogData.roomName || roomId,
                selectedDate: selectedDate.toLocaleDateString(),
                bookings: [],
                hasBookings: false,
                isLoading: false,
                error: true
            });
        }
    }

    /**
     * Opens a dialog to display existing room bookings
     */
    private async openExistingBookingsDialog(): Promise<void> {
        try {
            // Check if dialog already exists and destroy it first
            const existingDialog = this.byId("idExistingRoomBookingsDialog") as Dialog;
            if (existingDialog) {
                existingDialog.destroy();
            }

            const oDialog = (await Fragment.load({
                name: "de.realcore.realseats.view.fragment.ExistingRoomBookings",
                controller: this,
                id: this.getView().getId()
            })) as Dialog;

            // Ensure dialog has the model
            const existingBookingsModel = this.getView().getModel("ExistingRoomBookings");
            if (existingBookingsModel) {
                oDialog.setModel(existingBookingsModel, "ExistingRoomBookings");
            }

            this.getView().addDependent(oDialog);
            console.log("dlg", existingBookingsModel);
            oDialog.open();
        } catch (error) {
            Log.error("Error opening existing bookings dialog:", error as Error);
            MessageToast.show("Failed to open existing bookings view");
        }
    }

    /**
     * Load existing room bookings into the calendar model
     */
    public async onViewRoomBookingsCalendar(): Promise<void> {
        const dialogData = (this.getView().getModel("DialogData") as JSONModel).getData();

        if (!dialogData.isConferenceRoom) {
            Log.warning("View room bookings is only available for conference rooms");
            return;
        }

        const roomId = dialogData.roomID;
        const selectedDate = new Date(dialogData.date); // Date object

        // Initialize model
        const existingBookingsModel = new JSONModel({
            roomName: dialogData.roomName || roomId,
            selectedDate: selectedDate,
            bookings: [],
            hasBookings: false,
            isLoading: true
        });
        this.getView().setModel(existingBookingsModel, "ExistingRoomBookings");

        // Open the calendar dialog
        await this.openViewRoomBookingsCalendar();

        try {
            const startOfDay = new Date(selectedDate);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(selectedDate);
            endOfDay.setHours(23, 59, 59, 999);

            const startISO = formatter.formatLocalDateTime(startOfDay);
            const endISO = formatter.formatLocalDateTime(endOfDay);

            const apiService = this.getApiService();
            const allSeatId = await this.findAllSeatForRoom(roomId);

            if (!allSeatId) {
                existingBookingsModel.setData({
                    roomName: dialogData.roomName || roomId,
                    selectedDate,
                    bookings: [],
                    hasBookings: false,
                    isLoading: false
                });
                return;
            }

            const existingBookings = await apiService.booking.fetchByIdAndDate(
                { seatId: allSeatId },
                startISO,
                endISO
            );

            const activeBookings = existingBookings?.filter((b: any) => !b.cancelled) || [];

            // Convert startDate and endDate into real Date objects
            const formattedBookings = activeBookings.map((b: any) => ({
                id: b.id,
                startDate: new Date(b.startTime),
                endDate: new Date(b.endTime),
                employeeName: `${b.employee?.firstName || ""} ${b.employee?.lastName || ""}`.trim(),
                department: b.employee?.department?.description || "N/A",
                bookedBy:
                    `${b.bookingUser?.firstName || ""} ${b.bookingUser?.lastName || ""}`.trim()
            }));

            // Debug check: ensure startDate/endDate are Date objects
            console.log("Calendar Bookings:", formattedBookings);

            existingBookingsModel.setData({
                roomName: dialogData.roomName || roomId,
                selectedDate,
                bookings: formattedBookings,
                hasBookings: formattedBookings.length > 0,
                isLoading: false
            });
        } catch (error) {
            Log.error("Error loading room bookings for calendar:", error as Error);
            MessageToast.show("Failed to load existing room bookings");

            existingBookingsModel.setData({
                roomName: dialogData.roomName || roomId,
                selectedDate,
                bookings: [],
                hasBookings: false,
                isLoading: false,
                error: true
            });
        }
    }

    private async openViewRoomBookingsCalendar(): Promise<void> {
        try {
            const existingDialog = this.byId("idExistingRoomBookingsCalendarDialog") as Dialog;
            if (existingDialog) {
                existingDialog.destroy();
            }

            const oDialog = (await Fragment.load({
                name: "de.realcore.realseats.view.fragment.ExistingRoomBookingsCalendar",
                controller: this,
                id: this.getView().getId()
            })) as Dialog;

            const existingBookingsModel = this.getView().getModel("ExistingRoomBookings");
            if (existingBookingsModel) {
                oDialog.setModel(existingBookingsModel, "ExistingRoomBookings");
            }

            this.getView().addDependent(oDialog);
            oDialog.open();
        } catch (error) {
            Log.error("Error opening calendar dialog:", error as Error);
            UIFeedbackHelper.showErrorMessage("Failed to open existing bookings calendar");
        }
    }

    /**
     * Handle closing the existing room bookings dialog
     */
    public onCloseButtonExistingBookingsPress(): void {
        const oDialog =
            (this.byId("idExistingRoomBookingsDialog") as Dialog) ||
            (this.byId("idExistingRoomBookingsCalendarDialog") as Dialog);

        if (oDialog) {
            oDialog.close();
            oDialog.destroy();
        }
    }

    public onCloseExistingBookingsDialog(): void {
        // Keep the old method for backward compatibility
        this.onCloseButtonExistingBookingsPress();
    }

    public async onSaveAsFavorites(): Promise<void> {
        const RoomDialogModel = this.getView().getModel("SelectedSeatModel") as JSONModel;
        const deskData = RoomDialogModel.getData();

        const oResourceBundle = await this.getResourceBundle();
        MessageToast.show(
            oResourceBundle.getText("deskSavedAsFavorite", [deskData.roomID, deskData.seatID])
        );
    }

    public async onBookingOnBehalfChange(oEvent: Event): Promise<void> {
        const bSelected = (oEvent.getSource() as CheckBox).getSelected();
        const DialogDataModel = this.getView().getModel("DialogData") as JSONModel;
        DialogDataModel.setProperty("/forOthers", bSelected);

        // Load employees when booking on behalf is selected
        if (bSelected) {
            try {
                const employeeData =
                    await this.getApiService().employee.fetchEmployeesWithSuggestions();
                const employeeModel = new JSONModel(employeeData);
                this.getView().setModel(employeeModel, "employees");
                this.getView().setModel(employeeModel, "EmployeeModel");
            } catch (error) {
                Log.error("Error fetching employees with suggestions:", error as Error);
            }
        }
    }

    public onSelectedEmployeeInputValueHelpRequest(): void {
        // Open employee value help dialog
        this.openEmployeeValueHelpDialog();
    }

    private async openEmployeeValueHelpDialog(): Promise<void> {
        // Ensure employee data is loaded first
        await this.loadEmployeeData();

        // Load and open employee selection dialog using SelectDialog
        if (!this._employeeDialog) {
            const oSelectDialog = (await Fragment.load({
                name: "de.realcore.realseats.view.fragment.EmployeeValueHelp",
                controller: this,
                id: this.getView().getId() + "--employeeValueHelpDialog"
            })) as Dialog;

            this._employeeDialog = oSelectDialog;
            this.getView().addDependent(this._employeeDialog);
        }

        // Ensure the dialog has access to the employees model (already loaded)
        const employeesModel = this.getView().getModel("employees");
        console.log("Graphic - Employee model data:", (employeesModel as JSONModel)?.getData());
        if (employeesModel && this._employeeDialog) {
            this._employeeDialog.setModel(employeesModel, "employees");
            console.log("Graphic - Model set on dialog successfully");
        }

        // Reset selection
        this.selectedEmployeeFromDialog = null;

        this._employeeDialog.open();
    }

    public onAllEmployeesSelectDialogSearch(oEvent: Event): void {
        const sValue = ((oEvent as any).getParameter("value") as string) || "";
        const oFilter = new Filter({
            filters: [
                new Filter("firstName", FilterOperator.Contains, sValue),
                new Filter("lastName", FilterOperator.Contains, sValue),
                new Filter("department/name", FilterOperator.Contains, sValue)
            ],
            and: false
        });

        const oSelectDialog = oEvent.getSource() as SelectDialog;
        const oBinding = oSelectDialog.getBinding("items") as ListBinding;
        oBinding.filter(sValue ? [oFilter] : []);
    }

    public onAllEmployeesSelectDialogLiveChange(oEvent: Event): void {
        // Reuse the same search logic for live change
        this.onAllEmployeesSelectDialogSearch(oEvent);
    }

    public onAllEmployeesSelectDialogConfirm(oEvent: Event): void {
        const oSelectedItem = (oEvent as any).getParameter("selectedItem") as StandardListItem;
        if (oSelectedItem) {
            const oContext = oSelectedItem.getBindingContext("employees");
            if (oContext) {
                const oEmployee = oContext.getObject() as any;
                this.selectedEmployeeFromDialog = `${oEmployee.lastName}, ${oEmployee.firstName}`;
                this.selectedEmployeeId = oEmployee.id;

                // Update the input field in the main dialog
                const RoomDialogModel = this.getView().getModel("DialogData") as JSONModel;
                RoomDialogModel.setProperty("/selectedEmployee", this.selectedEmployeeFromDialog);

                console.log("Selected employee:", this.selectedEmployeeFromDialog);
            }
        }

        // Clean up
        if (this._employeeDialog) {
            this._employeeDialog.destroy();
            this._employeeDialog = null;
        }
    }

    public onAllEmployeesSelectDialogCancel(): void {
        // Clean up
        if (this._employeeDialog) {
            this._employeeDialog.destroy();
            this._employeeDialog = null;
        }
    }

    /**
     * Handle individual booking item press in the list
     */
    public onStandardListItemBookingPress(oEvent: Event): void {
        // Currently just for visual feedback - could be extended to show booking details
        const oItem = oEvent.getSource();
        console.log("Booking item pressed:", oItem);
    }

    /**
     * Handle cancel specific booking button press
     */
    public onButtonCancelSpecificBookingPress(oEvent: Event): Promise<void> {
        const oButton = oEvent.getSource() as Control;
        const oContext = oButton.getBindingContext("SelectedSeatModel");

        if (!oContext) {
            return Promise.resolve();
        }

        const bookingData = oContext.getObject();

        // Set the specific booking as selected and open cancellation dialog
        this.selectedSeatModel.setProperty("/selected", bookingData);

        return this.onButtonCancelBookingPress();
    }

    public onButtonCloseBookingDetailsPress(): void {
        this.onCloseBookingDetails();
    }

    public onCloseBookingDetails(): void {
        // Close the GraphicBooked dialog if it's open
        if (this._graphicBookedDialog) {
            this._graphicBookedDialog.close();
            this._graphicBookedDialog.destroy();
            this._graphicBookedDialog = null;
        }

        // Don't automatically close the SeatBookingDialog when closing GraphicBooked
        // This allows users to return to the booking dialog after viewing existing bookings
        // The SeatBookingDialog should only be closed when explicitly requested
    }

    public onCloseButtonPress(): void {
        // This is called when user explicitly clicks close button in SeatBookingDialog
        // In this case, we want to close all dialogs
        this.closeAllDialogs();
    }

    private closeAllDialogs(): void {
        // Close the GraphicBooked dialog if it's open
        if (this._graphicBookedDialog) {
            this._graphicBookedDialog.close();
            this._graphicBookedDialog.destroy();
            this._graphicBookedDialog = null;
        }

        // Close the SeatBookingDialog if it's open
        if (this._dialog) {
            this._dialog.close();
            this._dialog.destroy();
            this._dialog = null;
        }
    }

    /**
     * Handle table row deletion for all bookings table
     */
    public onAllBookingsTableDelete(oEvent: Event): void {
        console.log("onAllBookingsTableDelete called", oEvent);

        // Handle both button press events and table deletion events
        let oContext: any;

        // Try to get context from button press first
        const oButton = oEvent.getSource() as any;
        console.log("Button source:", oButton);

        if (oButton && typeof oButton.getBindingContext === "function") {
            oContext = oButton.getBindingContext("SelectedSeatModel");
            console.log("Got context from button:", oContext);
        }

        // Fallback to list item parameter for table deletion events
        if (!oContext) {
            const oListItem = (oEvent as any).getParameter("listItem");
            if (oListItem) {
                oContext = oListItem.getBindingContext("SelectedSeatModel");
                console.log("Got context from list item:", oContext);
            }
        }

        if (!oContext) {
            console.error("No context found for cancellation");
            MessageToast.show("Could not find booking to cancel");
            return;
        }

        const booking = oContext.getObject();
        console.log("Booking to cancel:", booking);

        if (!booking?.id) {
            console.error("No booking ID found:", booking);
            MessageToast.show("No booking ID found for cancellation");
            return;
        }

        // Check if user has permission to cancel this booking
        if (!booking.canCancel || booking.cancelled) {
            console.log(
                "Permission check failed. canCancel:",
                booking.canCancel,
                "cancelled:",
                booking.cancelled
            );
            MessageToast.show(
                "You don't have permission to cancel this booking or booking is already cancelled"
            );
            return;
        }

        console.log("Setting selected booking for cancellation");

        // Set the selected booking for cancellation dialog
        this.selectedSeatModel.setProperty("/selected", booking);

        // Open cancellation dialog
        console.log("Opening cancellation dialog");
        this.openCancellationDialog();
    }

    /**
     * Handle cancel booking button press
     */
    public async onButtonCancelBookingPress(): Promise<void> {
        const selectedBooking = this.selectedSeatModel?.getData()?.selected;
        if (!selectedBooking?.id) {
            MessageToast.show(this.getText("noBookingSelectedToCancel"));
            return;
        }

        // Open cancellation dialog directly without confirmation
        this.openCancellationDialog();
    }

    /**
     * Opens the cancellation dialog for the user to provide a cancellation reason
     */
    private openCancellationDialog(): void {
        const selectedBooking = this.selectedSeatModel?.getData()?.selected;
        if (!selectedBooking?.id) {
            Log.error("No booking selected for cancellation");
            return;
        }

        console.log("Opening cancellation dialog for booking:", selectedBooking);

        const oView = this.getView();

        // Create cancellation dialog if it doesn't exist - using same pattern as FreeSpacesSeats
        if (!this.cancelDialog) {
            Fragment.load({
                id: "graphic",
                name: "de.realcore.realseats.view.fragment.CancelBookingDialog",
                controller: this
            })
                .then((oFragment) => {
                    if (!oFragment) {
                        Log.error("Cancellation dialog fragment not loaded");
                        return;
                    }

                    this.cancelDialog = oFragment as Dialog;

                    const oDialogModel = new JSONModel(selectedBooking);
                    this.cancelDialog.setModel(oDialogModel, "BookingsModel");

                    const oContext = oDialogModel.createBindingContext("/");
                    this.cancelDialog.setBindingContext(oContext, "BookingsModel");

                    oView.addDependent(this.cancelDialog);
                    this.cancelDialog.open();
                })
                .catch((error) => {
                    Log.error("Failed to load cancellation dialog fragment:", error as Error);
                    // Fallback: directly cancel with default reason
                    this.performBookingCancellation(
                        selectedBooking.id,
                        "Cancelled from Graphic view"
                    );
                });
        } else {
            // Dialog already exists, just update the model and binding context
            const oDialogModel = new JSONModel(selectedBooking);
            this.cancelDialog.setModel(oDialogModel, "BookingsModel");

            // Create a binding context at the root path to match MyBookings pattern
            const oContext = oDialogModel.createBindingContext("/");
            this.cancelDialog.setBindingContext(oContext, "BookingsModel");

            this.cancelDialog.open();

            console.log("Reusing cancel dialog with booking data:", {
                bookingId: selectedBooking.id,
                roomName: selectedBooking.seat?.room?.name || "Unknown Room",
                seatName: selectedBooking.seat?.name || "Unknown Seat",
                startTime: selectedBooking.startTime,
                endTime: selectedBooking.endTime
            });
        }
    }

    /**
     * Handle confirmation of cancellation from the cancellation dialog
     */
    public async onConfirmCancellation(): Promise<void> {
        const oDialog = this.cancelDialog;
        const dialogModel = oDialog?.getModel("BookingsModel") as JSONModel;
        const bookingData = dialogModel?.getData();

        if (!bookingData?.id) {
            MessageToast.show(this.getText("noBookingSelectedToCancel"));
            return;
        }

        try {
            // Get cancellation reason from the model (automatically updated by the TextArea binding)
            // Handle undefined or empty cancellation reason
            const cancellationReason =
                bookingData.cancelDescription || "No reason provided for cancellation.";

            await this.performBookingCancellation(bookingData.id, cancellationReason);

            // Close the cancellation dialog
            if (this.cancelDialog) {
                this.cancelDialog.close();
            }
        } catch (error) {
            Log.error("Error during cancellation:", error as Error);
            const errorMessage = (error as Error).message || "Unknown cancellation error occurred";
            MessageToast.show(`Failed to cancel booking: ${errorMessage}`);
        }
    }

    /**
     * Handle cancel button press in the cancellation dialog (close without action)
     */
    public onCancelDialog(): void {
        if (this.cancelDialog) {
            this.cancelDialog.close();
        }
    }

    /**
     * Performs the actual booking cancellation after confirmation
     */
    private async performBookingCancellation(bookingId: string, reason?: string): Promise<void> {
        try {
            const apiService = this.getApiService();
            const oResourceBundle = await this.getResourceBundle();

            // Ensure we have a valid reason, fallback to default if empty or undefined
            const cancellationReason = reason?.trim() || "No reason provided for cancellation.";

            await apiService.booking.cancelBooking(bookingId, cancellationReason);

            // Clear booking caches since bookings have changed
            this.clearBookingCaches();

            // Close any open dialogs
            if (this._dialog) {
                this._dialog.close();
            }
            if (this._graphicBookedDialog) {
                this._graphicBookedDialog.close();
            }

            // Refresh the booking data and update the graphic
            await Promise.all([this.FetchBookingsSpaces(), this.fetchMyBookings()]);
            await this.markBookedSeats();

            MessageToast.show(oResourceBundle.getText("msgSuccessCancellation"));
        } catch (error) {
            Log.error("Error cancelling booking:", error as Error);
            const errorMessage = (error as Error).message || "Unknown cancellation error occurred";
            MessageToast.show(`Cancellation failed: ${errorMessage}`);
        }
    }

    /**
     * Adds canCancel property to booking data to determine if current user can cancel
     */
    private addCanCancelProperty(booking: any): void {
        try {
            const currentUserEmployeeId = this.getCurrentUserEmployeeId();

            // Debug logging
            console.log("=== DEBUG: Cancel Permission Check ===");
            console.log("Current user employee ID:", currentUserEmployeeId);
            console.log("Booking made by (bookingUser ID):", booking.bookingUser?.id);
            console.log("Booking for (employee ID):", booking.employee?.id);
            console.log("Booking user data:", booking.bookingUser);
            console.log("Employee data:", booking.employee);

            // User can cancel if:
            // 1. They made the booking (bookingUser.id matches current user)
            // 2. OR the booking is for them (employee.id matches current user)
            const canCancel =
                currentUserEmployeeId &&
                (booking.bookingUser?.id === currentUserEmployeeId ||
                    booking.employee?.id === currentUserEmployeeId);

            console.log("Can cancel result:", canCancel);
            console.log("========================================");

            booking.canCancel = canCancel;
        } catch (error) {
            Log.error("Error determining cancel permission:", error as Error);
            console.error("Full error details:", error);
            booking.canCancel = false;
        }
    }

    public onSelectedEmployeeInputSuggest(oEvent: Event): void {
        // Handle suggestion filtering
        const sValue = (
            ((oEvent as any).getParameter("suggestValue") as string) || ""
        ).toLowerCase();

        const oInput = oEvent.getSource() as Input;
        const aFilters: Filter[] = [];

        if (sValue) {
            const oFilter = new Filter({
                filters: [
                    new Filter("lastName", FilterOperator.Contains, sValue),
                    new Filter("firstName", FilterOperator.Contains, sValue),
                    new Filter("department/name", FilterOperator.Contains, sValue)
                ],
                and: false
            });
            aFilters.push(oFilter);
        }

        const oBinding = oInput.getBinding("suggestionItems") as ListBinding;
        if (oBinding) {
            oBinding.filter(aFilters);
        }
    }

    public onSelectedEmployeeInputSuggestionItemSelected(oEvent: Event): void {
        // Handle when user selects an employee from suggestions
        const oSelectedItem = (oEvent as any).getParameter("selectedItem") as Item;
        if (oSelectedItem) {
            const sSelectedText = oSelectedItem.getText();
            const DialogDataModel = this.getView().getModel("DialogData") as JSONModel;
            DialogDataModel.setProperty("/selectedEmployee", sSelectedText);
        }
    }

    // Methods copied from FreeSpacesSeats controller for dialog handling
    private setDialogDataFromSeat(seatData: any): void {
        const oDatePicker = this.getView().byId("idGraphicDatePicker") as DatePicker;
        const selectedDate = oDatePicker ? oDatePicker.getDateValue() : new Date();

        let dateValue: Date;
        if (selectedDate instanceof Date) {
            dateValue = selectedDate;
        } else {
            dateValue = new Date();
        }

        // Ensure dateValue is not before today (minimum allowed date)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dateValue < today) {
            // Use today if selected date is in the past
            dateValue = new Date();
        }

        // Calculate available time ranges based on current time
        const allTimeRanges = this.calculateAvailableTimeRanges(dateValue);

        // Filter time ranges based on existing bookings if any
        const availableTimeRanges =
            seatData.existingBookings && seatData.existingBookings.length > 0
                ? this.filterTimeRangesByExistingBookings(
                      allTimeRanges,
                      seatData.existingBookings,
                      dateValue
                  )
                : allTimeRanges;

        const defaultTimeRange =
            availableTimeRanges.length > 0 ? availableTimeRanges[0].key : TIME_RANGES.FULL_DAY;

        // Check if we're in flexible time mode (after business hours for today)
        const useFlexibleTime = defaultTimeRange === "flexible-time";

        // Set default start and end times for flexible time mode
        const defaultStartTime = useFlexibleTime ? getCurrentTimePlusMinutes(1) : "08:00";
        const defaultEndTime = useFlexibleTime ? "23:59" : "17:00";

        // Check if seat has existing bookings to show the View Bookings button
        const hasExistingBookings =
            seatData.existingBookings && seatData.existingBookings.length > 0;

        const dialogData = {
            seatId: seatData.id,
            roomID: seatData.room?.id || seatData.roomId || "",
            seatID: seatData.name || seatData.label || seatData.id,
            floor: seatData.room?.floorPlan?.name || "Ground Floor",
            department: seatData.room?.roomTypes?.[0]?.name || "General",
            roomTypes: seatData.room?.roomTypes?.map((rt: any) => rt.name) || [],
            roomRules: seatData.room?.roomRules || {},
            homeZones: seatData.room?.homeZones?.map((hz: any) => hz.name) || [],
            position: seatData.position || "",
            orientation: seatData.orientation || "VERTICAL",
            date: dateValue,
            minDate: new Date(), // Fresh Date object
            maxDate: new Date(getMaxBookingDate()), // Create fresh Date object to prevent mutation
            timeRange: defaultTimeRange,
            availableTimeRanges: availableTimeRanges,
            useFlexibleTime: useFlexibleTime, // Flag to show time pickers for regular seats when after hours
            startTime: defaultStartTime, // For flexible time selection
            endTime: defaultEndTime, // For flexible time selection
            forOthers: false,
            bringingDog: false,
            bringingChild: false,
            availabilityMessage: "Checking availability...",
            availabilityMessageType: "Information",
            isConferenceRoom: false,
            isCheckingAvailability: false,
            showViewBookingsButton: hasExistingBookings, // Show the view bookings button when seat has existing bookings
            room: seatData.room || {}, // Include the full room object for binding
            // Conference room booking status properties
            isRoomFullyBooked: false,
            roomBookingMessage: "",
            showRoomBookingWarning: false
        };

        const RoomDialogModel = new JSONModel(dialogData);
        this.getView().setModel(RoomDialogModel, "DialogData");

        // Check if this seat is in a conference room and if the room is fully booked
        this.checkConferenceRoomBookingStatus(dialogData);

        // Perform initial availability check
        this.checkDateAvailability(dateValue);
    }

    private async setDialogDataFromRoom(roomData: KonvaRoom): Promise<void> {
        const oDatePicker = this.getView().byId("idGraphicDatePicker") as DatePicker;
        const selectedDate = oDatePicker ? oDatePicker.getDateValue() : new Date();

        let dateValue: Date;
        if (selectedDate instanceof Date) {
            dateValue = selectedDate;
        } else {
            dateValue = new Date();
        }

        // Ensure dateValue is not before today (minimum allowed date)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dateValue < today) {
            // Use today if selected date is in the past
            dateValue = new Date();
        }

        // Get room data from cached data
        let fullRoomData: any = { id: roomData.id, name: roomData.id };
        let floorName = "Ground Floor";
        let departmentName = "General";

        // Get room details from the cached room data
        const roomDataModel = this.getView().getModel("RoomDataMap") as JSONModel;
        if (roomDataModel) {
            const cachedRoomData = roomDataModel.getProperty(`/${roomData.id}`);
            if (cachedRoomData) {
                fullRoomData = cachedRoomData;
                floorName = cachedRoomData.floorPlan?.name || "Ground Floor";
                departmentName = cachedRoomData.description || "General";
            }
        }

        // Set initial dialog data immediately
        const dialogData = {
            roomID: roomData.id,
            roomName: fullRoomData.name,
            floor: floorName,
            department: departmentName,
            date: dateValue,
            minDate: new Date(), // Fresh Date object
            maxDate: new Date(getMaxBookingDate()), // Create fresh Date object to prevent mutation
            startTime: "08:00", // Will be updated with optimal time
            endTime: "09:00", // Will be updated with optimal time
            forOthers: false,
            bringingDog: false,
            bringingChild: false,
            availabilityMessage: "Finding best available time slot...",
            availabilityMessageType: "Information",
            isCheckingAvailability: true,
            isConferenceRoom: true,
            showViewBookingsButton: true,
            room: fullRoomData
        };

        const RoomDialogModel = new JSONModel(dialogData);
        this.getView().setModel(RoomDialogModel, "DialogData");

        // Find optimal time slot and update dialog
        try {
            const { initialStartTime, cachedBookings } =
                await this.findFirstAvailableTimeSlotWithCache(roomData.id, dateValue);

            const initialEndTime = calculateEndTimeFromStart(initialStartTime);

            // Update dialog with optimal times
            RoomDialogModel.setProperty("/startTime", initialStartTime);
            RoomDialogModel.setProperty("/endTime", initialEndTime);
            RoomDialogModel.setProperty("/isCheckingAvailability", false);

            // Perform availability check for the room using cached data
            this.checkRoomAvailability(dateValue, initialStartTime, initialEndTime, cachedBookings);
        } catch (error) {
            console.error("Error finding optimal time slot:", error);
            // Update dialog to show error state
            RoomDialogModel.setProperty("/isCheckingAvailability", false);
            RoomDialogModel.setProperty(
                "/availabilityMessage",
                "Unable to check availability. Please select a time manually."
            );
            RoomDialogModel.setProperty("/availabilityMessageType", "Warning");
        }
    }

    /**
     * Validates and adjusts time range based on current time if booking is for today
     */
    private validateAndAdjustTimeRange(
        startTime: string,
        endTime: string,
        selectedDate: Date
    ): string {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const bookingDate = new Date(selectedDate);
        bookingDate.setHours(0, 0, 0, 0);

        // If not booking for today, return original time range
        if (bookingDate.getTime() !== today.getTime()) {
            return `${startTime}-${endTime}`;
        }

        // For today's booking, ensure start time is not in the past
        const currentTimePlusOne = getCurrentTimePlusMinutes(1);
        const [currentHour, currentMinute] = currentTimePlusOne.split(":").map(Number);
        const [startHour, startMinute] = startTime.split(":").map(Number);
        const [endHour, endMinute] = endTime.split(":").map(Number);

        const currentTotalMinutes = currentHour * 60 + currentMinute;
        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;

        // If start time has passed, adjust it to current time + 1 minute
        let adjustedStartTime = startTime;
        if (startTotalMinutes < currentTotalMinutes) {
            adjustedStartTime = currentTimePlusOne;
        }

        // If end time has also passed or is too close to start time, return empty (invalid)
        if (endTotalMinutes <= currentTotalMinutes) {
            return "";
        }

        return `${adjustedStartTime}-${endTime}`;
    }

    /**
     * Calculates available time ranges based on current time for today's bookings
     */
    private calculateAvailableTimeRanges(selectedDate: Date): Array<{ key: string; text: string }> {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const bookingDate = new Date(selectedDate);
        bookingDate.setHours(0, 0, 0, 0);

        // If not today, show all standard options with FULL_DAY first
        if (bookingDate.getTime() !== today.getTime()) {
            return [
                { key: TIME_RANGES.FULL_DAY, text: TIME_RANGES.FULL_DAY },
                { key: TIME_RANGES.MORNING, text: TIME_RANGES.MORNING },
                { key: TIME_RANGES.AFTERNOON, text: TIME_RANGES.AFTERNOON }
            ];
        }

        // For today, calculate what's still possible
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentTotalMinutes = currentHour * 60 + currentMinute;

        const availableRanges: Array<{ key: string; text: string }> = [];
        const addedKeys = new Set<string>(); // Track added keys to prevent duplicates

        // Parse business hours for calculations
        const morningEnd = parseInt(BUSINESS_HOURS.LUNCH_START.split(":")[0]) * 60;
        const afternoonStart = parseInt(BUSINESS_HOURS.LUNCH_END.split(":")[0]) * 60;
        const dayStart = parseInt(BUSINESS_HOURS.START.split(":")[0]) * 60;
        const dayEnd = parseInt(BUSINESS_HOURS.END.split(":")[0]) * 60;

        // Full day slot - prioritize this option first
        if (currentTotalMinutes < dayStart) {
            // Before business hours - show standard full day slot
            const fullDayKey = TIME_RANGES.FULL_DAY;
            if (!addedKeys.has(fullDayKey)) {
                availableRanges.push({ key: fullDayKey, text: fullDayKey });
                addedKeys.add(fullDayKey);
            }
        } else if (currentTotalMinutes < dayEnd) {
            // If we're within business hours, offer a "remaining day" option first
            const startTime = getCurrentTimePlusMinutes(1);
            const remainingDayKey = `${startTime}-${BUSINESS_HOURS.END}`;
            if (!addedKeys.has(remainingDayKey)) {
                availableRanges.push({
                    key: remainingDayKey,
                    text: remainingDayKey
                });
                addedKeys.add(remainingDayKey);
            }
        }

        // Morning slot - available if current time is before lunch
        if (currentTotalMinutes < morningEnd) {
            if (currentTotalMinutes < dayStart) {
                // Before business hours - show standard morning slot
                const morningKey = TIME_RANGES.MORNING;
                if (!addedKeys.has(morningKey)) {
                    availableRanges.push({ key: morningKey, text: morningKey });
                    addedKeys.add(morningKey);
                }
            } else {
                // During morning hours - show adjusted morning slot
                const startTime = getCurrentTimePlusMinutes(1);
                const adjustedMorningKey = `${startTime}-${BUSINESS_HOURS.LUNCH_START}`;
                if (!addedKeys.has(adjustedMorningKey)) {
                    availableRanges.push({
                        key: adjustedMorningKey,
                        text: adjustedMorningKey
                    });
                    addedKeys.add(adjustedMorningKey);
                }
            }
        }

        // Afternoon slot - available if current time is before end of day
        if (currentTotalMinutes < dayEnd) {
            if (currentTotalMinutes < afternoonStart) {
                // Before afternoon - show standard afternoon slot
                const afternoonKey = TIME_RANGES.AFTERNOON;
                if (!addedKeys.has(afternoonKey)) {
                    availableRanges.push({ key: afternoonKey, text: afternoonKey });
                    addedKeys.add(afternoonKey);
                }
            } else {
                // During afternoon - show adjusted afternoon slot
                const startTime = getCurrentTimePlusMinutes(1);
                const adjustedAfternoonKey = `${startTime}-${BUSINESS_HOURS.END}`;
                if (!addedKeys.has(adjustedAfternoonKey)) {
                    availableRanges.push({
                        key: adjustedAfternoonKey,
                        text: adjustedAfternoonKey
                    });
                    addedKeys.add(adjustedAfternoonKey);
                }
            }
        }

        // If no ranges are available (e.g., after business hours), enable flexible time selection
        if (availableRanges.length === 0) {
            availableRanges.push({ key: "flexible-time", text: "Flexible time selection" });
        }

        return availableRanges;
    }

    /**
     * Filters time ranges by removing those that conflict with existing bookings
     * @param timeRanges Available time ranges to filter
     * @param existingBookings Array of existing bookings for the seat
     * @param selectedDate The selected date
     * @returns Filtered time ranges that don't conflict with existing bookings
     */
    private filterTimeRangesByExistingBookings(
        timeRanges: Array<{ key: string; text: string }>,
        existingBookings: any[],
        selectedDate: Date
    ): Array<{ key: string; text: string }> {
        if (!existingBookings || existingBookings.length === 0) {
            return timeRanges;
        }

        // Filter out cancelled bookings and bookings for different dates
        const relevantBookings = existingBookings.filter((booking) => {
            if (booking.cancelled) {
                return false;
            }

            const bookingStart = new Date(booking.startTime);
            const bookingDate = new Date(bookingStart);
            bookingDate.setHours(0, 0, 0, 0);

            const selectedDateNormalized = new Date(selectedDate);
            selectedDateNormalized.setHours(0, 0, 0, 0);

            return bookingDate.getTime() === selectedDateNormalized.getTime();
        });

        if (relevantBookings.length === 0) {
            return timeRanges;
        }

        // Filter out time ranges that conflict with existing bookings
        const availableRanges = timeRanges.filter((range) => {
            if (!range.key.includes("-")) {
                return true;
            }

            const [startTime, endTime] = range.key.split("-");
            const rangeStart = parseTimeToMinutes(startTime);
            const rangeEnd = parseTimeToMinutes(endTime);

            return !relevantBookings.some((booking) => {
                const bookingStart = new Date(booking.startTime);
                const bookingEnd = new Date(booking.endTime);
                const bookingStartMinutes =
                    bookingStart.getHours() * 60 + bookingStart.getMinutes();
                const bookingEndMinutes = bookingEnd.getHours() * 60 + bookingEnd.getMinutes();

                return rangeStart < bookingEndMinutes && rangeEnd > bookingStartMinutes;
            });
        });

        // If no standard ranges are available, add flexible time option
        if (availableRanges.length === 0) {
            return [
                {
                    key: "flexible-time",
                    text: "Flexible time selection - book around existing bookings"
                }
            ];
        }

        return availableRanges;
    }

    /**
     * Validates and adjusts start time based on current time if booking is for today
     */
    private validateAndAdjustStartTime(startTime: string, selectedDate: Date): string {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const bookingDate = new Date(selectedDate);
        bookingDate.setHours(0, 0, 0, 0);

        // If booking is for today, ensure start time is not in the past
        if (bookingDate.getTime() === today.getTime()) {
            const currentTimePlusOne = getCurrentTimePlusMinutes(1);
            const [currentHour, currentMinute] = currentTimePlusOne.split(":").map(Number);
            const [startHour, startMinute] = startTime.split(":").map(Number);

            const currentTotalMinutes = currentHour * 60 + currentMinute;
            const startTotalMinutes = startHour * 60 + startMinute;

            if (startTotalMinutes < currentTotalMinutes) {
                return currentTimePlusOne;
            }
        }

        return startTime;
    }

    /**
     * Finds the first available time slot for a conference room starting from 8:00 AM
     */
    private async findFirstAvailableTimeSlot(roomId: string, selectedDate: Date): Promise<string> {
        const { initialStartTime } = await this.findFirstAvailableTimeSlotWithCache(
            roomId,
            selectedDate
        );
        return initialStartTime;
    }

    /**
     * Finds the first available time slot for a conference room starting from 8:00 AM and returns cached bookings
     */
    private async findFirstAvailableTimeSlotWithCache(
        roomId: string,
        selectedDate: Date
    ): Promise<{ initialStartTime: string; cachedBookings: any[] }> {
        try {
            // Start checking from 8:00 AM
            const startHour = 8;
            const maxHour = 18; // Check until 6 PM

            // Find the "ALL" seat for this room
            const allSeatId = await this.findAllSeatForRoom(roomId);
            if (!allSeatId) {
                // If no ALL seat found, return default time
                return {
                    initialStartTime: this.validateAndAdjustStartTime("08:00", selectedDate),
                    cachedBookings: []
                };
            }

            // Create cache key
            const dateKey = selectedDate.toDateString();
            const cacheKey = `${roomId}_${dateKey}`;

            let activeBookings: any[] = [];

            // Check if we have cached bookings for this room and date
            if (this._cachedRoomBookings[cacheKey]) {
                activeBookings = this._cachedRoomBookings[cacheKey];
            } else {
                // Get start and end of the selected day
                const startOfDay = new Date(selectedDate);
                startOfDay.setHours(0, 0, 0, 0);
                const endOfDay = new Date(selectedDate);
                endOfDay.setHours(23, 59, 59, 999);

                const startISO = formatter.formatLocalDateTime(startOfDay);
                const endISO = formatter.formatLocalDateTime(endOfDay);

                const apiService = this.getApiService();

                // Fetch all existing bookings for the day
                const existingBookings = await apiService.booking.fetchByIdAndDate(
                    { seatId: allSeatId },
                    startISO,
                    endISO
                );

                // Filter out cancelled bookings and cache the result
                activeBookings = existingBookings
                    ? existingBookings.filter((booking: any) => !booking.cancelled)
                    : [];

                this._cachedRoomBookings[cacheKey] = activeBookings;
            }

            // Check each hour slot starting from 8 AM
            for (let hour = startHour; hour < maxHour; hour++) {
                const timeSlot = `${hour.toString().padStart(2, "0")}:00`;
                const nextHour = `${(hour + 1).toString().padStart(2, "0")}:00`;

                // Validate against current time if booking is for today
                const adjustedTimeSlot = this.validateAndAdjustStartTime(timeSlot, selectedDate);

                // If time was adjusted beyond this hour, skip to next
                if (adjustedTimeSlot !== timeSlot) {
                    const [adjustedHour] = adjustedTimeSlot.split(":").map(Number);
                    if (adjustedHour > hour) {
                        continue;
                    }
                }

                // Create date objects for this time slot
                const slotStart = new Date(selectedDate);
                const slotEnd = new Date(selectedDate);

                const [startHourNum, startMinute] = adjustedTimeSlot.split(":").map(Number);
                const [endHourNum, endMinute] = nextHour.split(":").map(Number);

                slotStart.setHours(startHourNum, startMinute, 0, 0);
                slotEnd.setHours(endHourNum, endMinute, 0, 0);

                // Check if this slot conflicts with any existing booking
                const hasConflict = activeBookings.some((booking: any) => {
                    const bookingStart = new Date(booking.startTime);
                    const bookingEnd = new Date(booking.endTime);

                    // Check for overlap
                    return slotStart < bookingEnd && slotEnd > bookingStart;
                });

                if (!hasConflict) {
                    return {
                        initialStartTime: adjustedTimeSlot,
                        cachedBookings: activeBookings
                    };
                }
            }

            // If no slot is available, return the last checked time (user will see availability error)
            return {
                initialStartTime: this.validateAndAdjustStartTime("18:00", selectedDate),
                cachedBookings: activeBookings
            };
        } catch (error) {
            Log.error("Error finding first available time slot:", error as Error);
            // Fallback to default time
            return {
                initialStartTime: this.validateAndAdjustStartTime("08:00", selectedDate),
                cachedBookings: []
            };
        }
    }

    public onStartTimeChange(oEvent: Event): void {
        const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
        if (oDialogModel) {
            let startTime = (oEvent.getSource() as any).getValue();
            const selectedDate = oDialogModel.getProperty("/date");
            let endTime = oDialogModel.getProperty("/endTime");
            // Validate and adjust start time if booking is for today
            startTime = validateAndAdjustStartTime(startTime, selectedDate);

            // If start time >= end time, set end time to start time + 1 hour
            if (isTimeGreaterOrEqual(startTime, endTime)) {
                endTime = calculateEndTimeFromStart(startTime);
                oDialogModel.setProperty("/endTime", endTime);
            }

            oDialogModel.setProperty("/startTime", startTime);
            oDialogModel.setProperty("/useFlexibleTime", true);

            // Re-check availability when time changes
            this.checkRoomAvailability(selectedDate, startTime, endTime);

            // Also check conference room booking status when time changes
            const dialogData = oDialogModel.getData();
            this.checkConferenceRoomBookingStatus(dialogData);
        }
    }

    public onEndTimeChange(oEvent: Event): void {
        const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
        if (oDialogModel) {
            let endTime = (oEvent.getSource() as any).getValue();
            const startTime = oDialogModel.getProperty("/startTime");

            // If end time <= start time, set end time to start time + 1 hour
            if (isTimeGreaterOrEqual(startTime, endTime)) {
                endTime = calculateEndTimeFromStart(startTime);
            }

            oDialogModel.setProperty("/endTime", endTime);
            oDialogModel.setProperty("/useFlexibleTime", true);

            // Re-check availability when time changes
            const selectedDate = oDialogModel.getProperty("/date");
            this.checkRoomAvailability(selectedDate, startTime, endTime);

            // Also check conference room booking status when time changes
            const dialogData = oDialogModel.getData();
            this.checkConferenceRoomBookingStatus(dialogData);
        }
    }

    private async checkRoomAvailability(
        selectedDate: Date,
        startTime: string,
        endTime: string,
        cachedBookings?: any[]
    ): Promise<void> {
        const DialogDataModel = this.getView().getModel("DialogData") as JSONModel;
        if (!DialogDataModel) {
            return;
        }

        const roomId = DialogDataModel.getProperty("/roomID");
        if (!roomId) {
            return;
        }

        try {
            const startDateTime = new Date(selectedDate);
            const endDateTime = new Date(selectedDate);

            const [startHour, startMinute] = startTime.split(":").map(Number);
            const [endHour, endMinute] = endTime.split(":").map(Number);

            startDateTime.setHours(startHour, startMinute, 0, 0);
            endDateTime.setHours(endHour, endMinute, 0, 0);

            // Validate time range
            if (endDateTime <= startDateTime) {
                DialogDataModel.setProperty(
                    "/availabilityMessage",
                    "End time must be after start time"
                );
                DialogDataModel.setProperty("/availabilityMessageType", "Error");
                DialogDataModel.setProperty("/isCheckingAvailability", false);
                return;
            }

            // Show busy indicator and disable confirm button
            DialogDataModel.setProperty("/isCheckingAvailability", true);
            DialogDataModel.setProperty("/availabilityMessage", "Checking room availability...");
            DialogDataModel.setProperty("/availabilityMessageType", "Information");

            const apiService = this.getApiService();
            const startISO = formatter.formatLocalDateTime(startDateTime);
            const endISO = formatter.formatLocalDateTime(endDateTime);

            // Determine availability for the room correctly
            // 1) Prefer using the special "ALL" seat if it exists (represents room-level booking)
            const allSeatId = await this.findAllSeatForRoom(roomId);
            let isAvailable: boolean | null = null;

            if (allSeatId) {
                const result = await apiService.seat.checkSeatAvailability(
                    allSeatId,
                    startISO,
                    endISO
                );
                isAvailable = result.available;
            } else {
                // 2) Fallback: check if at least one seat in the room is available for the timeslot
                try {
                    const seatsInRoom = await apiService.room.fetchSeatsByRoom(roomId);
                    if (seatsInRoom && seatsInRoom.length > 0) {
                        // Check seats sequentially until one free seat is found
                        isAvailable = false;
                        for (const seat of seatsInRoom) {
                            if (!seat?.id) {
                                continue;
                            }
                            const res = await apiService.seat.checkSeatAvailability(
                                seat.id,
                                startISO,
                                endISO
                            );
                            if (res.available) {
                                isAvailable = true;
                                break;
                            }
                        }
                    }
                } catch (e) {
                    Log.error(
                        "Error fetching seats for room while checking availability:",
                        e as Error
                    );
                    isAvailable = null;
                }
            }

            // Hide busy indicator
            DialogDataModel.setProperty("/isCheckingAvailability", false);

            if (isAvailable) {
                DialogDataModel.setProperty(
                    "/availabilityMessage",
                    "Room is available for the selected time."
                );
                DialogDataModel.setProperty("/availabilityMessageType", "Success");
            } else if (!isAvailable) {
                DialogDataModel.setProperty(
                    "/availabilityMessage",
                    "Room is not available for the selected time."
                );
                DialogDataModel.setProperty("/availabilityMessageType", "Error");
            } else {
                DialogDataModel.setProperty(
                    "/availabilityMessage",
                    "Unable to determine availability at the moment."
                );
                DialogDataModel.setProperty("/availabilityMessageType", "Warning");
            }
        } catch (error) {
            // Hide busy indicator on error
            DialogDataModel.setProperty("/isCheckingAvailability", false);
            DialogDataModel.setProperty("/availabilityMessage", "Error checking availability");
            DialogDataModel.setProperty("/availabilityMessageType", "Error");
            console.error("Error checking room availability:", error);
        }
    }

    public onDatePickerBookingDateChange(oEvent: Event): void {
        const oDatePicker = oEvent.getSource() as DatePicker;
        const selectedDate = oDatePicker.getDateValue();

        if (selectedDate) {
            // Check if this is a conference room dialog
            const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
            const isConferenceRoom = oDialogModel?.getProperty("/isConferenceRoom");

            if (isConferenceRoom) {
                // For conference rooms, get the start and end times and check room availability
                const startTime = oDialogModel.getProperty("/startTime") || "08:00";
                const endTime = oDialogModel.getProperty("/endTime") || "09:00";
                this.checkRoomAvailability(selectedDate, startTime, endTime);
            } else {
                // For regular seats, update available time ranges for the new date
                if (oDialogModel) {
                    const availableTimeRanges = this.calculateAvailableTimeRanges(selectedDate);
                    oDialogModel.setProperty("/availableTimeRanges", availableTimeRanges);

                    // Check if we need to switch to/from flexible time mode
                    const newDefaultTimeRange =
                        availableTimeRanges.length > 0
                            ? availableTimeRanges[0].key
                            : TIME_RANGES.FULL_DAY;
                    const newUseFlexibleTime = newDefaultTimeRange === "flexible-time";

                    // Update flexible time mode and related properties
                    oDialogModel.setProperty("/useFlexibleTime", newUseFlexibleTime);

                    if (newUseFlexibleTime) {
                        // Switching to flexible time mode
                        oDialogModel.setProperty("/startTime", getCurrentTimePlusMinutes(1));
                        oDialogModel.setProperty("/endTime", "23:59");
                        oDialogModel.setProperty("/timeRange", "flexible-time");
                    } else {
                        // Reset to first available time range if current selection is no longer valid
                        const currentTimeRange = oDialogModel.getProperty("/timeRange");
                        const isCurrentValid = availableTimeRanges.some(
                            (range) => range.key === currentTimeRange
                        );
                        if (!isCurrentValid && availableTimeRanges.length > 0) {
                            oDialogModel.setProperty("/timeRange", availableTimeRanges[0].key);
                        }
                    }
                }

                // Use the regular availability check for seats
                this.checkDateAvailability(selectedDate);
            }
        }
    }

    public onTimeRangeChange(oEvent: Event): void {
        // Get current date from dialog model
        const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
        const selectedDate = oDialogModel.getProperty("/date");

        if (selectedDate) {
            this.checkDateAvailability(selectedDate);

            // Also check conference room booking status when time range changes
            const dialogData = oDialogModel.getData();
            this.checkConferenceRoomBookingStatus(dialogData);
        }
    }

    private async checkDateAvailability(selectedDate: Date): Promise<void> {
        const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
        const oResourceBundle = (this.getView()?.getModel("i18n") as any)?.getResourceBundle();

        // Show checking message by setting model property for message strip
        oDialogModel.setProperty("/isCheckingAvailability", true);
        oDialogModel.setProperty("/availabilityMessageType", "Information");
        oDialogModel.setProperty(
            "/availabilityMessage",
            oResourceBundle.getText("dateAvailabilityChecking")
        );

        try {
            const useFlexibleTime = oDialogModel.getProperty("/useFlexibleTime");
            let startTimeStr: string;
            let endTimeStr: string;

            if (useFlexibleTime) {
                // For flexible time mode, get times from startTime/endTime properties
                startTimeStr =
                    oDialogModel.getProperty("/startTime") || getCurrentTimePlusMinutes(1);
                endTimeStr = oDialogModel.getProperty("/endTime") || "23:59";
            } else {
                // For regular time slots, parse from timeRange
                const timeRange = oDialogModel.getProperty("/timeRange") || TIME_RANGES.FULL_DAY;
                if (timeRange.includes("-")) {
                    [startTimeStr, endTimeStr] = timeRange.split("-");
                } else {
                    // Fallback for invalid timeRange
                    startTimeStr = "08:00";
                    endTimeStr = "17:00";
                }
            }

            // Validate and adjust start time if booking is for today and time has passed
            const adjustedStartTime = this.validateAndAdjustStartTime(startTimeStr, selectedDate);

            // Format date strings for API
            const startDate = new Date(selectedDate);
            const endDate = new Date(selectedDate);

            startDate.setHours(
                parseInt(adjustedStartTime.split(":")[0]),
                parseInt(adjustedStartTime.split(":")[1]) || 0,
                0,
                0
            );
            endDate.setHours(
                parseInt(endTimeStr.split(":")[0]),
                parseInt(endTimeStr.split(":")[1]) || 0,
                0,
                0
            );

            const startISO = formatter.formatLocalDateTime(startDate);
            const endISO = formatter.formatLocalDateTime(endDate);

            // Update the dialog model with adjusted time if it changed
            if (useFlexibleTime && adjustedStartTime !== startTimeStr) {
                oDialogModel.setProperty("/startTime", adjustedStartTime);
                console.log(`Start time adjusted to: ${adjustedStartTime}`);
            } else if (!useFlexibleTime && adjustedStartTime !== startTimeStr) {
                const adjustedTimeRange = `${adjustedStartTime}-${endTimeStr}`;
                oDialogModel.setProperty("/timeRange", adjustedTimeRange);
                console.log(`Time range adjusted to: ${adjustedTimeRange}`);
            }

            const seatID = oDialogModel.getProperty("/seatId");

            // Use the corrected seat availability API
            const apiService = this.getApiService();
            const result = await apiService.seat.checkSeatAvailability(seatID, startISO, endISO);

            // Clear busy state and show result
            oDialogModel.setProperty("/isCheckingAvailability", false);

            oDialogModel.setData({
                ...oDialogModel.getData(),
                availabilityMessage: result.available
                    ? oResourceBundle.getText("dateAvailable")
                    : oResourceBundle.getText("seatAlreadyBooked"),
                availabilityMessageType: result.available ? "Success" : "Error"
            });
        } catch (error) {
            console.error("Error checking date availability:", error);
            // Clear busy state and show error
            oDialogModel.setProperty("/isCheckingAvailability", false);

            oDialogModel.setData({
                ...oDialogModel.getData(),
                availabilityMessage: oResourceBundle.getText("dateAvailabilityError"),
                availabilityMessageType: "Error"
            });
        }
    }

    private async loadEmployeeData(): Promise<void> {
        const apiService = this.getApiService();

        // Load employee data from API for the SeatBookingDialog fragment
        try {
            const employeeData = await apiService.employee.fetchEmployeesWithSuggestions();

            // Create a unified list with recent employees first
            const allEmployees: any[] = [];

            // Add suggested employees first with isRecent flag
            if (employeeData.suggestedEmployees) {
                employeeData.suggestedEmployees.forEach((emp: any) => {
                    allEmployees.push({ ...emp, isRecent: true });
                });
            }

            // Add remaining employees (not suggested) with isRecent false
            if (employeeData.employees) {
                employeeData.employees.forEach((emp: any) => {
                    // Only add if not already in suggested list
                    if (
                        !employeeData.suggestedEmployees?.some(
                            (suggested: any) => suggested.id === emp.id
                        )
                    ) {
                        allEmployees.push({ ...emp, isRecent: false });
                    }
                });
            }

            const unifiedEmployeeModel = new JSONModel({
                employees: employeeData.employees || [], // Keep original for backward compatibility
                suggestedEmployees: employeeData.suggestedEmployees || [], // Keep original for backward compatibility
                allEmployees: allEmployees
            });

            this.getView().setModel(unifiedEmployeeModel, "employees");
            this.getView().setModel(unifiedEmployeeModel, "EmployeeModel");
        } catch (error) {
            console.error("Error loading employee data:", error);
            const fallbackModel = new JSONModel({
                employees: [],
                suggestedEmployees: [],
                allEmployees: []
            });
            this.getView().setModel(fallbackModel, "employees");
        }
    }

    // Keep the Konva stage sized to the available container so the floorplan is draggable across the whole page
    private _resizeStageToContainer(): void {
        if (!this._stage) {
            return;
        }
        const bottomPadding = 16;
        const fallbackTopOffset = 100;
        const minStageHeightThreshold = 50;
        const minStageHeightFallback = 200;
        const fallbackTotalOffset = 250;
        const wrapperEl = document.getElementById("graphic-wrapper") as HTMLElement | null;
        const containerEl = document.getElementById("graphic") as HTMLElement | null;
        const el = wrapperEl || containerEl;
        const rect = el?.getBoundingClientRect?.();

        // Store old dimensions and viewport state before resize
        const oldWidth = this._stage.width();
        const oldHeight = this._stage.height();
        const oldScale = this._stage.scaleX();
        const oldPos = this._stage.position();

        // Calculate viewport center in content space (before resize)
        const oldCenterX = (oldWidth / 2 - oldPos.x) / oldScale;
        const oldCenterY = (oldHeight / 2 - oldPos.y) / oldScale;

        const width = Math.max(
            0,
            Math.floor(wrapperEl?.clientWidth || rect?.width || window.innerWidth)
        );
        let height: number;
        if (rect) {
            const top = rect.top || 0;
            height = Math.floor(window.innerHeight - top - bottomPadding);
        } else {
            height = Math.max(0, Math.floor(window.innerHeight - fallbackTopOffset));
        }
        if (height < minStageHeightThreshold) {
            height = Math.max(minStageHeightFallback, window.innerHeight - fallbackTotalOffset);
        }

        // Check if this is the initial setup (scale is 1 and position is default)
        const isInitialSetup = oldScale === 1 && oldPos.x === 0 && oldPos.y === 0;

        this._stage.size({ width, height });

        if (isInitialSetup) {
            // First time setup - apply design scale normally
            this._applyDesignScale();
        } else {
            // Preserve viewport: keep the same center point visible after resize
            // Calculate new position to maintain the same content center
            const newPos = {
                x: width / 2 - oldCenterX * oldScale,
                y: height / 2 - oldCenterY * oldScale
            };
            this._stage.position(newPos);
        }

        this._stage.batchDraw();
    }

    private readonly _onWindowResize = (): void => {
        this._resizeStageToContainer();
    };

    /**
     * Sets up a MutationObserver to watch for DynamicPage header state changes
     * and recalculates canvas size when the header expands/collapses
     */
    private _setupDynamicPageObserver(): void {
        console.log("Setting up DynamicPage observer for header state changes");
        const dynamicPage = this.byId("GraphicDynamicPage");
        if (!dynamicPage) {
            return;
        }

        // Watch for changes in the header expanded state
        const checkHeaderState = (): void => {
            const isExpanded = (dynamicPage as any).getHeaderExpanded();
            if (this._lastHeaderExpandedState !== isExpanded) {
                this._lastHeaderExpandedState = isExpanded;
                this._resizeStageToContainer();
            }
        };

        // Poll for header state changes (since there's no built-in event)
        this._headerStateCheckInterval = window.setInterval(checkHeaderState, 200);
    }

    private _lastHeaderExpandedState: boolean | undefined = undefined;
    private _headerStateCheckInterval: number | undefined = undefined;

    // Scale the design-sized content group to match the current stage size without stretching
    private _applyDesignScale(): void {
        if (!this._stage || !this._contentGroup) {
            return;
        }
        const stageW = this._stage.width();
        const stageH = this._stage.height();
        const sx = stageW / this.DESIGN_BASE.WIDTH;
        const sy = stageH / this.DESIGN_BASE.HEIGHT;
        // Use uniform scale to preserve aspect ratio and avoid stretching
        // Also cap the initial scale to a maximum of 1 to prevent upscaling (over-zoomed initial view)
        const uniform = Math.min(sx, sy, 1);

        // Center the content within the stage (letterbox/pillarbox as needed)
        const contentW = this.DESIGN_BASE.WIDTH * uniform;
        const contentH = this.DESIGN_BASE.HEIGHT * uniform;
        const offsetX = Math.max(0, (stageW - contentW) / 2);
        const offsetY = Math.max(0, (stageH - contentH) / 2);

        this._contentGroup.scale({ x: uniform, y: uniform });
        this._contentGroup.position({ x: offsetX, y: offsetY });

        // Apply the same transformations to search highlight layer
        if (this._searchHighlightLayer) {
            this._searchHighlightLayer.scale({ x: uniform, y: uniform });
            this._searchHighlightLayer.position({ x: offsetX, y: offsetY });
        }
    }

    public setupInteractiveGraphic(): void {
        // Ensure stage is initialized
        if (!this._stage) {
            console.warn("Konva stage not initialized yet");
            return;
        }

        // Variables for touch zoom
        let lastCenter: { x: number; y: number } | null = null;
        let lastDist = 0;
        let isPinching = false;

        // Helper functions for touch calculations
        function getDistance(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
            return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
        }

        function getCenter(
            p1: { x: number; y: number },
            p2: { x: number; y: number }
        ): { x: number; y: number } {
            return {
                x: (p1.x + p2.x) / 2,
                y: (p1.y + p2.y) / 2
            };
        }

        // Mouse wheel zoom for desktop with improved limits and smoother experience
        this._stage.on("wheel", (e) => {
            e.evt.preventDefault();

            const oldScale = this._stage.scaleX();
            const pointer = this._stage.getPointerPosition();

            if (!pointer) {
                return;
            }

            const mousePointTo = {
                x: (pointer.x - this._stage.x()) / oldScale,
                y: (pointer.y - this._stage.y()) / oldScale
            };

            const direction = e.evt.deltaY > 0 ? -1 : 1;
            // Smoother zoom factor
            const factor = 1.08;
            const newScale = direction > 0 ? oldScale * factor : oldScale / factor;

            // Improved zoom limits with better min/max values
            const minZoom = 0.05;
            const maxZoom = 10.0;
            const limitedScale = Math.max(minZoom, Math.min(newScale, maxZoom));

            this._stage.scale({ x: limitedScale, y: limitedScale });

            const newPos = {
                x: pointer.x - mousePointTo.x * limitedScale,
                y: pointer.y - mousePointTo.y * limitedScale
            };

            this._stage.position(newPos);
            this._stage.batchDraw();
        });

        // Enable stage dragging for single touch/mouse
        this._stage.draggable(true);

        // Override touch events for pinch zoom
        const container = this._stage.container();

        container.addEventListener("touchstart", (e) => {
            if (e.touches.length === 2) {
                // Two fingers - start pinch zoom
                isPinching = true;
                this._isPinchZooming = true; // Set class flag
                this._stage.draggable(false);

                const touch1 = e.touches[0];
                const touch2 = e.touches[1];

                const p1 = { x: touch1.clientX, y: touch1.clientY };
                const p2 = { x: touch2.clientX, y: touch2.clientY };

                lastCenter = getCenter(p1, p2);
                lastDist = getDistance(p1, p2);
            } else {
                // Single finger - enable dragging
                isPinching = false;
                this._isPinchZooming = false; // Clear class flag
                this._stage.draggable(true);
            }
        });
        container.addEventListener("touchmove", (e) => {
            if (isPinching && e.touches.length === 2) {
                e.preventDefault();

                const touch1 = e.touches[0];
                const touch2 = e.touches[1];

                const p1 = { x: touch1.clientX, y: touch1.clientY };
                const p2 = { x: touch2.clientX, y: touch2.clientY };

                const newCenter = getCenter(p1, p2);
                const dist = getDistance(p1, p2);

                if (lastCenter && lastDist > 0) {
                    const scale = this._stage.scaleX();

                    // Get the point relative to the stage
                    const stageRect = container.getBoundingClientRect();
                    const centerRelativeToStage = {
                        x: lastCenter.x - stageRect.left,
                        y: lastCenter.y - stageRect.top
                    };

                    const pointTo = {
                        x: (centerRelativeToStage.x - this._stage.x()) / scale,
                        y: (centerRelativeToStage.y - this._stage.y()) / scale
                    };

                    const newScale = Math.max(0.05, Math.min((scale * dist) / lastDist, 10));

                    this._stage.scale({ x: newScale, y: newScale });

                    // Adjust position to zoom around touch center
                    const newPos = {
                        x: centerRelativeToStage.x - pointTo.x * newScale,
                        y: centerRelativeToStage.y - pointTo.y * newScale
                    };

                    this._stage.position(newPos);
                    this._stage.batchDraw();
                }

                lastCenter = newCenter;
                lastDist = dist;
            }
        });

        container.addEventListener("touchend", (e) => {
            if (e.touches.length < 2) {
                isPinching = false;
                this._stage.draggable(true);
                lastCenter = null;
                lastDist = 0;

                // Clear pinch zoom flag with a small delay to prevent accidental clicks
                setTimeout(() => {
                    this._isPinchZooming = false;
                }, 100);
            }
        });

        container.addEventListener("touchcancel", () => {
            isPinching = false;
            this._stage.draggable(true);
            lastCenter = null;
            lastDist = 0;
            this._isPinchZooming = false;
        });
    }

    public async onSearch(): Promise<void> {
        const oDatePicker = this.byId("idGraphicDatePicker") as DatePicker;
        const selectedDate = oDatePicker?.getDateValue();

        if (selectedDate) {
            this.oDate = selectedDate;
            this.startTime = getStartOfDay(selectedDate);
            this.endTime = getEndOfDay(selectedDate);
        }

        this._clearFloorplan();
        await this.loadFloorplan();

        if (selectedDate) {
            await Promise.all([this.FetchBookingsSpaces(), this.fetchMyBookings()]);
            await this.markBookedSeats();
        }
    }

    // New event handlers for enhanced filter functionality
    public onFilterBarGraphicSearch(): void {
        void this.onSearch();
    }

    private _lastDateChange: Date | null = null;
    private readonly _dateChangeDebounceMs = 500; // 500ms debounce

    public onDatePickerGraphicChange(): void {
        const oDatePicker = this.byId("idGraphicDatePicker") as DatePicker;
        const dateValue = oDatePicker?.getValue();

        if (!dateValue) {
            Log.warning("No date selected in date picker");
            return;
        }

        // Parse date string (dd-MM-yyyy format) to avoid timezone issues
        const parts = dateValue.split("-");
        if (parts.length !== 3) {
            Log.error(`Invalid date format from DatePicker: ${dateValue}`);
            return;
        }

        const newDate = parseDatePickerValue(dateValue);

        // Debounce rapid date changes
        if (
            this._lastDateChange &&
            Date.now() - this._lastDateChange.getTime() < this._dateChangeDebounceMs
        ) {
            return;
        }

        // Check if date actually changed
        if (this.oDate && newDate.getTime() === this.oDate.getTime()) {
            return;
        }

        this._lastDateChange = new Date();

        // Set date properties
        this.oDate = newDate;
        this.startTime = getStartOfDay(newDate);
        this.endTime = getEndOfDay(newDate);

        // Clear caches and refresh data
        this.clearBookingCaches();

        Promise.all([this.FetchBookingsSpaces(), this.fetchMyBookings()])
            .then(async () => {
                await this.markBookedSeats();
            })
            .catch((error) => {
                Log.error("Error fetching bookings after date change:", error as Error);
            });
    }

    public async onItemsComboBoxLocationFloorSelectionChange(): Promise<void> {
        // When location/floor selection changes, reload the floorplan and booking data
        await this.onFloorSelectionChange();

        try {
            // Fetch booking data and then mark the seats with correct colors
            await Promise.all([this.FetchBookingsSpaces(), this.fetchMyBookings()]);
            // After both booking data requests complete and floorplan is loaded, mark the booked seats
            await this.markBookedSeats();
        } catch (error) {
            Log.error("Error fetching bookings after location/floor change:", error as Error);
        }
    }

    public onSearchFieldGraphicDeskSearch(oEvent: Event): void {
        const searchValue = (((oEvent as any).getParameter("query") as string) || "").toLowerCase();
        this.highlightSearchedDesk(searchValue);
    }

    public onSearchFieldGraphicDeskLiveChange(oEvent: Event): void {
        const searchValue = (
            ((oEvent as any).getParameter("newValue") as string) || ""
        ).toLowerCase();
        this.highlightSearchedDesk(searchValue);
    }

    /**
     * Updates search highlight colors for theme changes
     */
    private updateSearchHighlightTheme(): void {
        if (!this._contentGroup) {
            return;
        }

        const highlightColor = this.isDarkMode() ? "#FFD700" : "#FF8C00";

        // Update search highlights in room groups
        const roomGroups = this._contentGroup.find("Group") as Konva.Group[];
        roomGroups.forEach((group) => {
            const highlights = group.find(".searchHighlight") as Konva.Rect[];
            highlights.forEach((highlight) => {
                highlight.stroke(highlightColor);
            });
        });

        // Also update any remaining highlights in the dedicated layer for backward compatibility
        if (this._searchHighlightLayer) {
            this._searchHighlightLayer.getChildren().forEach((child) => {
                if (child instanceof Konva.Rect) {
                    const rect = child as Konva.Rect;
                    rect.stroke(highlightColor);
                }
            });
            this._searchHighlightLayer.batchDraw();
        }

        this._layer.batchDraw();
    }

    /**
     * Updates existing visual elements for theme changes
     */
    private updateExistingElementsTheme(): void {
        if (!this._layer) {
            return;
        }

        // Update conference room backgrounds and borders
        const roomGroups = this._layer.find("Group");
        roomGroups.forEach((group) => {
            const roomType = group.getAttr("type");
            if (roomType === "meetingroom") {
                const konvaGroup = group as Konva.Group;
                // Update conference room styling
                const boundary = konvaGroup.findOne("Rect") as Konva.Rect;
                if (boundary) {
                    // Use visible room background color for both light and dark themes
                    boundary.fill("#415159");
                    // Update border color for theme
                    const strokeColor = this.isDarkMode() ? "white" : "black";
                    boundary.stroke(strokeColor);
                }

                // Update room label colors
                const label = konvaGroup.findOne("Text") as Konva.Text;
                if (label) {
                    label.fill(this.isDarkMode() ? "white" : "black");
                }
            }
        });

        // Update seat colors (they will maintain their status colors but update stroke)
        const seats = this._layer.find('Rect[name="seat"]');
        seats.forEach((seat) => {
            const konvaSeat = seat as Konva.Rect;
            const seatStrokeColor = this.isDarkMode() ? "white" : "black";
            konvaSeat.stroke(seatStrokeColor);

            // Update seat label text color
            const textLabel = konvaSeat.getAttr("textLabel") as Konva.Text;
            if (textLabel) {
                textLabel.fill(this.isDarkMode() ? "white" : "black");
            }
        });

        this._layer.batchDraw();
    }

    /**
     * Restores the normal color of a seat based on its booking status
     * @param shape The seat shape to restore color for
     */
    private restoreSeatColor(shape: Konva.Rect): void {
        // Use fresh availability data if available, otherwise fall back to seat attributes
        const seatId = shape.getAttr("id");
        const availabilityData = seatId ? this.getPreCalculatedSeatData(seatId) : null;

        if (shape.getAttr("isSelfBooked")) {
            shape.fill(this.isDarkMode() ? "#002794" : "#1e52e2");
        } else if (shape.getAttr("isUnauthorized")) {
            shape.fill("grey");
        } else if (availabilityData?.isFullyBooked || shape.getAttr("isFullyBooked")) {
            shape.fill("#E63946");
        } else if (availabilityData?.isPartial || shape.getAttr("isPartiallyBooked")) {
            shape.fill("#E6B800");
        } else if (shape.getAttr("isBooked")) {
            shape.fill("#E63946");
        } else {
            shape.fill("#34A853");
        }
    }

    /**
     * Creates a search highlight shape around a seat shape
     * @param seatShape The seat shape to highlight
     */
    private highlightSeatBorder(seatShape: Konva.Rect): void {
        // Instead of adding to a separate layer, add the highlight directly to the seat's parent
        // This ensures perfect alignment since it shares the same coordinate space
        const roomGroup = seatShape.getParent() as Konva.Group;
        if (!roomGroup) {
            return;
        }

        // Get seat position and dimensions relative to its parent (room group)
        const seatX = seatShape.x();
        const seatY = seatShape.y();
        const seatWidth = seatShape.width();
        const seatHeight = seatShape.height();

        const padding = 1; // Minimal padding around the seat for dense highlighting
        const highlightColor = this.isDarkMode() ? "#FFD700" : "#FF8C00"; // Gold in dark mode, orange in light mode

        // Create rounded rectangle highlight that matches the seat shape better
        const highlightRect = new Konva.Rect({
            x: seatX - padding,
            y: seatY - padding,
            width: seatWidth + padding * 2,
            height: seatHeight + padding * 2,
            cornerRadius: 3,
            stroke: highlightColor,
            strokeWidth: 2,
            fill: "transparent",
            listening: false, // Don't interfere with seat interaction
            opacity: 0.9,
            name: "searchHighlight" // Tag for easy identification and cleanup
        });

        // Store reference to the seat ID for cleanup
        highlightRect.setAttr("seatId", seatShape.id());

        // Add to the same parent as the seat for perfect alignment
        roomGroup.add(highlightRect);

        // Move the highlight behind the seat
        highlightRect.moveToBottom();
        // But keep it above any room background
        const roomBackground = roomGroup.findOne("Rect");
        if (roomBackground && roomBackground !== seatShape) {
            highlightRect.moveUp();
        }
    }

    /**
     * Clears all search highlight indicators
     */
    private clearSearchHighlights(): void {
        if (!this._contentGroup) {
            return;
        }

        // Find all search highlight shapes in room groups and remove them
        const roomGroups = this._contentGroup.find("Group") as Konva.Group[];
        roomGroups.forEach((group) => {
            const highlights = group.find(".searchHighlight") as Konva.Rect[];
            highlights.forEach((highlight) => highlight.destroy());
        });

        // Also clear any remaining highlights in the dedicated layer for backward compatibility
        if (this._searchHighlightLayer) {
            this._searchHighlightLayer.removeChildren();
            this._searchHighlightLayer.batchDraw();
        }

        // Redraw the main layer to reflect changes
        this._layer.batchDraw();
    }

    /**
     * Adds corner indicators to a shape on hover
     * @param shape The shape to add corner indicators to
     * @param parent The parent group to add the indicators to
     */
    private addCornerIndicators(shape: Konva.Rect, parent: Konva.Group): void {
        const width = shape.width();
        const height = shape.height();
        const cornerSize = Math.min(width, height) * 0.15; // 15% of smaller dimension
        const strokeWidth = 2;
        const cornerColor = this.isDarkMode() ? "white" : "black"; // White for dark mode, black for light mode

        // Create corner lines
        const corners = [
            // Top-left corner
            { x1: 0, y1: 0, x2: cornerSize, y2: 0 },
            { x1: 0, y1: 0, x2: 0, y2: cornerSize },
            // Top-right corner
            { x1: width - cornerSize, y1: 0, x2: width, y2: 0 },
            { x1: width, y1: 0, x2: width, y2: cornerSize },
            // Bottom-left corner
            { x1: 0, y1: height - cornerSize, x2: 0, y2: height },
            { x1: 0, y1: height, x2: cornerSize, y2: height },
            // Bottom-right corner
            { x1: width, y1: height - cornerSize, x2: width, y2: height },
            { x1: width - cornerSize, y1: height, x2: width, y2: height }
        ];

        corners.forEach((corner, index) => {
            const line = new Konva.Line({
                points: [corner.x1, corner.y1, corner.x2, corner.y2],
                stroke: cornerColor,
                strokeWidth: strokeWidth,
                lineCap: "round",
                listening: false,
                name: "hoverCorner" // Tag for easy removal
            });
            parent.add(line);
        });
    }

    /**
     * Removes corner indicators from a group
     * @param parent The parent group to remove indicators from
     */
    private removeCornerIndicators(parent: Konva.Group): void {
        // Find and destroy all corner indicator lines
        const corners = parent.find(".hoverCorner");
        corners.forEach((corner) => corner.destroy());
    }

    private highlightSearchedDesk(searchValue: string): void {
        // Clear existing search highlights
        this.clearSearchHighlights();

        if (!searchValue || searchValue.trim() === "") {
            return;
        }

        // Get all seat shapes
        const deskrects = this._layer.find("Rect") as Konva.Rect[];
        const searchLower = searchValue.toLowerCase();

        deskrects.forEach((shape) => {
            const seatId = shape.id();
            const seatLabel = shape.getAttr("label") || "";

            // More precise matching logic
            const isMatch = this.isSearchMatch(seatId, seatLabel, searchLower);

            if (isMatch) {
                // Create highlight around matching seat
                this.highlightSeatBorder(shape);
            }
        });

        // Redraw the main layer to show all highlights
        this._layer.batchDraw();
    }

    /**
     * Determines if a seat matches the search criteria
     * @param seatId The seat ID
     * @param seatLabel The seat label
     * @param searchValue The search value (lowercase)
     * @returns true if the seat matches the search
     */
    private isSearchMatch(seatId: string, seatLabel: string, searchValue: string): boolean {
        const idLower = seatId.toLowerCase();
        const labelLower = seatLabel.toLowerCase();

        // Try exact match first (most precise)
        if (idLower === searchValue || labelLower === searchValue) {
            return true;
        }

        // Handle multi-part searches (e.g., "R033 D4", "R033D4", "D4-R033")
        if (this.isMultiPartSearch(searchValue)) {
            return this.matchesMultiPartSearch(idLower, labelLower, searchValue);
        }

        // Only match if it appears as a complete segment (after dash or at start/end)
        const segments = [
            ...idLower.split("-"),
            ...idLower.split("_"),
            ...idLower.split(" "),
            ...labelLower.split("-"),
            ...labelLower.split("_"),
            ...labelLower.split(" ")
        ];

        if (segments.some((segment) => segment === searchValue)) {
            return true;
        }

        // Try word boundary matches (e.g., "d1" matches "d1" but not "d10" or "ad1")
        const wordBoundaryRegex = new RegExp(
            `\\b${searchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
            "i"
        );
        if (wordBoundaryRegex.test(seatId) || wordBoundaryRegex.test(seatLabel)) {
            return true;
        }

        // Try start-of-string match only for longer terms
        if (searchValue.length >= 3) {
            if (idLower.startsWith(searchValue) || labelLower.startsWith(searchValue)) {
                return true;
            }
        }

        // For very short search terms (1-2 characters), only allow exact segment matches
        if (searchValue.length <= 2) {
            return false;
        }

        // For longer search terms, allow contains matching
        return idLower.includes(searchValue) || labelLower.includes(searchValue);
    }

    /**
     * Checks if the search value contains multiple parts (room + seat combination)
     * @param searchValue The search value to check
     * @returns true if it's a multi-part search
     */
    private isMultiPartSearch(searchValue: string): boolean {
        // Check for space-separated parts (e.g., "R033 D4")
        if (searchValue.includes(" ")) {
            return true;
        }

        // Check for room+seat pattern without separators (e.g., "R033D4")
        const roomSeatPattern = /^[a-z]\d+[a-z]\d+$/i;
        if (roomSeatPattern.test(searchValue)) {
            return true;
        }

        // Check for dash-separated parts (e.g., "D4-R033")
        if (searchValue.includes("-") && searchValue.split("-").length === 2) {
            return true;
        }

        return false;
    }

    /**
     * Matches multi-part searches against seat ID and label
     * @param idLower Seat ID in lowercase
     * @param labelLower Seat label in lowercase
     * @param searchValue Search value in lowercase
     * @returns true if matches multi-part search
     */
    private matchesMultiPartSearch(
        idLower: string,
        labelLower: string,
        searchValue: string
    ): boolean {
        const targets = [idLower, labelLower];

        // Handle space-separated search (e.g., "R033 D4")
        if (searchValue.includes(" ")) {
            const searchParts = searchValue.split(" ").filter((part) => part.length > 0);
            return targets.some((target) => {
                return searchParts.every((part) => {
                    const segments = [
                        ...target.split("-"),
                        ...target.split("_"),
                        ...target.split(" ")
                    ];
                    return segments.some((segment) => segment === part) || target.includes(part);
                });
            });
        }

        // Handle combined pattern without separators (like "R033D4")
        const roomSeatPattern = /^([a-z]\d+)([a-z]\d+)$/i;
        const match = roomSeatPattern.exec(searchValue);
        if (match) {
            const [, roomPart, seatPart] = match;
            return targets.some((target) => {
                // Check if target contains both parts
                const segments = [...target.split("-"), ...target.split("_"), ...target.split(" ")];
                const hasRoomPart =
                    segments.some((segment) => segment === roomPart) || target.includes(roomPart);
                const hasSeatPart =
                    segments.some((segment) => segment === seatPart) || target.includes(seatPart);
                return hasRoomPart && hasSeatPart;
            });
        }

        // Handle dash-separated search (like "D4-R033")
        if (searchValue.includes("-")) {
            const searchParts = searchValue.split("-").filter((part) => part.length > 0);
            return targets.some((target) => {
                return searchParts.every((part) => {
                    const segments = [
                        ...target.split("-"),
                        ...target.split("_"),
                        ...target.split(" ")
                    ];
                    return segments.some((segment) => segment === part) || target.includes(part);
                });
            });
        }

        return false;
    }

    /**
     * Gets optimized booking status text for tooltips using pre-calculated data
     * @param seatId The seat ID to get booking status for
     * @returns Booking status text with minimal performance impact
     */
    private getBookingStatus(seatId: string): string {
        const seatData = this.getPreCalculatedSeatData(seatId);

        if (!seatData?.hasBooking) {
            return "Available";
        }

        if (seatData.isFullyBooked) {
            return "Fully booked";
        }

        if (seatData.isPartial) {
            const availableSlots = [];
            if (seatData.morning) {
                availableSlots.push("Morning");
            }
            if (seatData.afternoon) {
                availableSlots.push("Afternoon");
            }
            return `Partially booked\n${availableSlots.join(" & ")} available`;
        }

        return "Booked";
    }

    /**
     * Gets detailed booking information for tooltips (lazy-loaded)
     * Only called when tooltip is actually displayed to avoid performance impact
     * @param seatId The seat ID to get detailed info for
     * @returns Detailed booking status text with clean formatting
     */
    private getDetailedBookingInfo(seatId: string): string {
        const bookedSeatsData = this.BookedSeatsModel?.getData()?.bookedSeats?.seats || [];
        const seatBookingData = bookedSeatsData.find((seat: any) => seat.seatId === seatId);

        if (!seatBookingData?.bookedTimes?.length) {
            return "Available for booking";
        }

        // Only do expensive processing when tooltip is actually shown
        const bookings = seatBookingData.bookedTimes.slice(0, 3); // Limit to first 3 for performance

        const bookingLines = bookings.map((booking: any, index: number) => {
            const name =
                booking.givenName && booking.surname
                    ? `${booking.givenName} ${booking.surname}`
                    : "Unknown User";

            const startTime = new Date(booking.startTime);
            const endTime = new Date(booking.endTime);
            const timeRange = `${startTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${endTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;

            // Use tree structure: ├─ for middle items, └─ for last item
            const isLast = index === bookings.length - 1 && seatBookingData.bookedTimes.length <= 3;
            const prefix = isLast ? " └─" : " ├─";

            return `${prefix} ${name} (${timeRange})`;
        });

        if (seatBookingData.bookedTimes.length > 3) {
            bookingLines.push(`└─ ... and ${seatBookingData.bookedTimes.length - 3} more`);
        }

        // Format with booking icon and tree structure
        return `📅 Current Bookings:\n${bookingLines.join("\n")}`;
    }

    /**
     * Formats seat information for tooltip display
     * @param seatData The seat data to format
     * @param seatShape The Konva seat shape to get booking status from
     * @returns Formatted text for tooltip
     */
    private formatSeatTooltip(seatData: KonvaSeat, seatShape: Konva.Rect): string {
        const isBooked = seatShape.getAttr("isBooked") || false;
        const isSelfBooked = seatShape.getAttr("isSelfBooked") || false;
        const isUnauthorized = seatShape.getAttr("isUnauthorized") || false;
        const isPartiallyBooked = seatShape.getAttr("isPartiallyBooked") || false;

        // Get room information from the seat data map
        const seatDataModel = this.getView().getModel("SeatDataMap") as JSONModel;
        const fullSeatData = seatDataModel?.getProperty(`/${seatData.id}`) || seatData;
        const position = fullSeatData.position || "";

        let statusIcon: string;
        let statusText: string;
        if (isSelfBooked) {
            statusIcon = "👤";
            statusText = "Booked by you";
        } else if (isPartiallyBooked) {
            statusIcon = "🟡";
            statusText = "Partially Booked\n" + this.getDetailedBookingInfo(seatData.id);
        } else if (isBooked) {
            statusIcon = "🔴";
            statusText = "Booked\n" + this.getDetailedBookingInfo(seatData.id);
        } else if (isUnauthorized) {
            statusIcon = "🚫";
            statusText = "Not authorized";
        } else {
            statusIcon = "🟢";
            statusText = "Available";
        }

        let tooltipText = `🪑 ${seatData.label || seatData.id}`;

        if (position) {
            tooltipText += `\n📋 ${position}`;
        }

        tooltipText += `\n${statusIcon} ${statusText}`;

        return tooltipText;
    }

    /**
     * Formats conference room information for tooltip display
     * @param roomData The room data to format
     * @param roomShape The Konva room shape to get booking status from
     * @returns Formatted text for tooltip
     */
    private formatConferenceRoomTooltip(roomData: KonvaRoom, roomShape: Konva.Rect): string {
        const isBooked = roomShape.getAttr("isBooked") || false;

        // Get room details from the cached room data
        const roomDataModel = this.getView().getModel("RoomDataMap") as JSONModel;
        const fullRoomData = roomDataModel?.getProperty(`/${roomData.id}`) || roomData;

        const roomName = fullRoomData.name || roomData.name || "Conference Room";
        const description = fullRoomData.description || "";

        let tooltipText = `🏢 Bookable Room\n📝 ${roomName}`;

        if (description && description.trim() !== "") {
            tooltipText += `\n📄 ${description}`;
        }

        if (!isBooked) {
            tooltipText += `\n\n💡 Click to book this room`;
        }

        return tooltipText;
    }

    /**
     * Shows DOM tooltip at the specified screen position with smart positioning
     * @param x X coordinate for tooltip (screen coordinates)
     * @param y Y coordinate for tooltip (screen coordinates)
     * @param text Text to display in tooltip
     */
    private showTooltip(x: number, y: number, text: string): void {
        if (!this._domTooltip) {
            return;
        }

        // Update theme if needed
        this.updateDOMTooltipTheme();

        // Set tooltip content - use textContent to avoid HTML parsing issues
        this._domTooltip.textContent = text.trim();

        // Smart positioning to prevent tooltip from going off-screen
        const tooltipOffset = 15;
        const padding = 10;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Get tooltip dimensions after setting content
        this._domTooltip.style.visibility = "hidden";
        this._domTooltip.style.display = "block";
        const tooltipRect = this._domTooltip.getBoundingClientRect();
        const tooltipWidth = tooltipRect.width;
        const tooltipHeight = tooltipRect.height;

        // Calculate optimal position
        let tooltipX = x + tooltipOffset;
        let tooltipY = y - tooltipOffset;

        // Adjust horizontal position if tooltip would go off-screen
        if (tooltipX + tooltipWidth > viewportWidth - padding) {
            tooltipX = x - tooltipWidth - tooltipOffset; // Show on left side
        }
        if (tooltipX < padding) {
            tooltipX = padding; // Keep within left edge
        }

        // Adjust vertical position if tooltip would go off-screen
        if (tooltipY < padding) {
            tooltipY = y + tooltipOffset; // Show below cursor
        }
        if (tooltipY + tooltipHeight > viewportHeight - padding) {
            tooltipY = viewportHeight - tooltipHeight - padding; // Keep within bottom edge
        }

        // Position tooltip
        this._domTooltip.style.left = tooltipX + "px";
        this._domTooltip.style.top = tooltipY + "px";

        // Show tooltip
        this._domTooltip.style.visibility = "visible";
        this._domTooltip.style.opacity = "0.95";
    }

    /**
     * Hides the DOM tooltip
     */
    private hideTooltip(): void {
        if (this._domTooltip) {
            this._domTooltip.style.visibility = "hidden";
            this._domTooltip.style.opacity = "0";
        }
    }

    /**
     * Clean up EventBus subscriptions when controller is destroyed
     */
    public onExit(): void {
        // Clear search highlights and stop animations
        this.clearSearchHighlights();

        // Unsubscribe from theme change events to prevent memory leaks
        this.getOwnerComponent()
            .getEventBus()
            .unsubscribe("App", "ThemeChanged", this.onThemeChanged.bind(this));

        // Remove window resize listener
        window.removeEventListener("resize", this._onWindowResize);

        // Clear header state check interval
        if (this._headerStateCheckInterval !== undefined) {
            window.clearInterval(this._headerStateCheckInterval);
            this._headerStateCheckInterval = undefined;
        }

        // Clean up DOM tooltip
        if (this._domTooltip) {
            this._domTooltip.remove();
            this._domTooltip = null;
        }

        // Clean up dialogs if they exist
        if (this._dialog) {
            this._dialog.destroy();
            this._dialog = null;
        }
        if (this._graphicBookedDialog) {
            this._graphicBookedDialog.destroy();
            this._graphicBookedDialog = null;
        }
        if (this._employeeDialog) {
            this._employeeDialog.destroy();
            this._employeeDialog = null;
        }
        if (this.cancelDialog) {
            this.cancelDialog.destroy();
            this.cancelDialog = null;
        }
    }

    private _clearFloorplan(): void {
        // Clear search highlights when clearing floorplan
        this.clearSearchHighlights();

        if (this._contentGroup) {
            this._contentGroup.removeChildren();
        } else {
            this._layer.destroyChildren();
            this._contentGroup = new Konva.Group({ x: 0, y: 0 });
            this._layer.add(this._contentGroup);
        }
    }
}
